import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { Client, Room, getStateCallbacks } from "colyseus.js";
import { HelloState, sightRadius, type VisionSource, ABILITIES, ITEMS, type BuildingType, isPetSpecies, getMaterial, TRADE_RESOURCES, TRADE_PRICES, parseTradeAction, type Cube, type Building, getMod, MASTERY_MOD_KEY } from "@tribez/shared";
import { createFog, type FogApi } from "./fog";
import { createMinimap, type MinimapApi } from "./minimap";
import { loadWorld, MAP_UNITS, type World } from "./world";
import { createPlayer, type Player, TINT_OWN, TINT_ALLY, TINT_ENEMY, TINT_DUMMY, tintFor } from "./player";
import { updateXrayOcclusion } from "./xray-silhouette";
import { canopyFadeUniforms, foliageWind } from "./toon";
import { createCameraDebug, applyStoredCameraDebug, type CameraDebugApi, type CameraDebugPanel } from "./hud/camera-debug";
import { createResourceRenderer, type ResourceRenderer } from "./resources";
import { createHud, type HudAPI } from "./hud";
import { createCreatureRender, type CreatureRenderManager } from "./creatures/creature-render";
import { createCanopyFadeController, type CanopyFadeController } from "./world/canopy-fade";
import { createProjectileRender, type ProjectileRenderManager } from "./projectiles/projectile-render";
import { createBuildingRender, type BuildingRenderManager } from "./buildings/building-render";
import { createPlacementPreview, type PlacementPreview } from "./buildings/placement-preview";
import { createZMenu, type ZMenu } from "./buildings/z-menu";
import { createBuildingMenu, type BuildingMenu, type MenuOpts } from "./buildings/building-menu";
import { createWonderHud, type WonderHud } from "./buildings/wonder-hud";
import { createEndScreen, type EndScreen } from "./buildings/end-screen";
import { createMainPlate, type MainPlate } from "./hud/main-plate";
import { createPetFrame, type PetFrame } from "./hud/pet-frame";
import { createGoldCounter, type GoldCounter } from "./hud/gold-counter";
import { createHelpPanel, type HelpPanel } from "./hud/help-panel";
import { createSettingsPanel, type SettingsPanel } from "./hud/settings-panel";
import { createFpsOverlay } from "./hud/fps-overlay";
import { getCameraSpeedMultiplier } from "./hud/camera-settings";
import { getEdgePanEnabled } from "./hud/edge-pan-settings";
import { effectivePixelRatio } from "./hud/resolution-settings";
import { createCookProgress, type CookProgress } from "./hud/cook-progress";
import { createExpertiseArc, type ExpertiseArc } from "./hud/expertise-arc";
import { createOffscreenHero, type OffscreenHero } from "./hud/offscreen-hero";
import { createDirectionArrows, type DirectionArrows } from "./hud/direction-arrows";
import { createOnboarding, type Onboarding } from "./hud/onboarding";
import { createCraftMenu, findNearbyCraftBuilding, type CraftMenu } from "./items/craft-menu";
import { createTavernShop, findNearbyTavern, type TavernShop } from "./abilities/tavern-shop";
// pet-mastery-panel exports are now consumed by craft-menu.ts (unified Lodge menu, Task 7).
import { createCorpseLoot, findNearbyCorpse, type CorpseLoot } from "./items/corpse-loot";
import { createCorpseRender, type CorpseRenderManager } from "./items/corpse-render";
import { renderWalkStateIcon } from "./hud/walk-state-icon";
import { installLayoutEditor } from "./hud/layout-editor";
import { createCastCursor, type CastCursor } from "./hud/cast-cursor";
import { createCursorController, type CursorState } from "./hud/cursor";
import { createAbilityTargetMarker, type AbilityTargetMarker } from "./hud/ability-target-marker";
import { createFloaterManager, type FloaterManager } from "./floaters/manager";
import { installCastVfx, type CastVfxAPI } from "./abilities/cast-vfx";
import { createBeam, createPointBurst, type VfxInstance } from "./abilities/cast-vfx-shapes";
import { tickDashTrailVfx, clearDashTrailVfx } from "./items/item-effect-vfx";
import { createDayNightVisual, type DayNightVisual } from "./day-night-visual";
import { createHorizon } from "./world/horizon";
import { createDayNightClock, type DayNightClock } from "./day-night-clock";
import { createWinReadout, type WinReadout } from "./win-readout";
import {
  createHoverTooltip, type HoverTooltip, type HoverContent, type HoverRelation,
  cubeHover, creatureHover, buildingHover, resourceHover,
} from "./hud/hover-tooltip";
import {
  createInspectPanel, type InspectPanel, type InspectRelation,
  inspectCube, inspectPet,
} from "./hud/inspect-panel";
import { createLanding, type RoomRow } from "./menu/landing";
import { createLobbyScreen } from "./menu/lobby";
import { createLoadingScreen } from "./menu/loading-screen";
import { loadAudioManifest } from "./audio/manifest";
import { AudioManager } from "./audio/audio-manager";
import { installAudioTriggers } from "./audio/triggers";
import { installAudioDirector, type AudioDirectorAPI } from "./audio/director";
import { LatencySim } from "./net/latency-sim";
import { SnapshotBuffer } from "./net/snapshot-interp";

// Touch-only device gate (HARD BLOCK). The game is mouse + keyboard only
// (right-click move, edge-pan, Q/W/E/R, Z build menu), so on a phone/tablet we
// show a banner and halt before booting three.js + the WS connect. There is no
// "continue anyway" bypass: the game is genuinely unplayable on touch, and a
// cold visitor (e.g. a Reddit/CrazyGames link opened on a phone) should hit a
// clean "open on desktop" message, not a dead game. `(hover: none) and
// (pointer: coarse)` matches primary-touch devices while leaving touch-enabled
// laptops (which also have a fine/hover pointer) alone.
if (matchMedia("(hover: none) and (pointer: coarse)").matches) {
  document.getElementById("mobile-banner")!.classList.remove("hidden");
  // Halt module evaluation — nothing below this should run on a touch device.
  throw new Error("[tribez] touch-only device — desktop required");
}

const canvas = document.getElementById("app") as HTMLCanvasElement;
const statusEl = document.getElementById("status")!;
// The old top-of-screen hint strip is gone — controls now live behind the "?"
// Help button (hud/help-panel.ts), created in the game-init below.

const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
// Pixel ratio is the dominant fill-rate cost on hi-DPI displays; the player-tunable
// resolution scale (Settings → Display) rides on top of the ≤2 hi-DPI clamp. Default
// scale is 1.0, so this is identical to the original `min(devicePixelRatio, 2)`.
renderer.setPixelRatio(effectivePixelRatio());
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
// We drive shadow re-renders on a cadence (see SHADOW_UPDATE_INTERVAL in the frame
// loop) rather than every frame. The full caster set (trees/buildings/units) is the
// biggest GPU sink after foliage culling; the sun direction is fixed, so refreshing
// the shadow map every other frame is imperceptible but roughly halves that cost.
renderer.shadowMap.autoUpdate = false;
// ACES filmic tone mapping with a slight exposure bump — the no-tone-mapping default
// renders flat-shaded vertex colors with a washed-out response curve. ACES gives the
// frame the warm, contrasted look users associate with "polished 3D" without bloom or
// post-processing. Output color space is sRGB (r152+ default; explicit for clarity).
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.05;
renderer.outputColorSpace = THREE.SRGBColorSpace;

const scene = new THREE.Scene();
// Primal re-grade DAY values — kept in sync with the DAY keyframe in
// day-night-visual.ts (dusty-warm horizon, was pale-blue 0xc8d8e4).
scene.background = new THREE.Color(0xd7b483);
scene.fog = new THREE.FogExp2(0xd7b483, 0.008);

// FOV 40 (was 50): narrower = flatter, more isometric "MOBA" perspective, closer
// to LoL's 40° — less wide-angle distortion (Aviv camera tuning, 2026-06-29).
const camera = new THREE.PerspectiveCamera(40, window.innerWidth / window.innerHeight, 0.1, 2000);
const camCenter = new THREE.Vector3(MAP_UNITS / 2, 0, MAP_UNITS / 2);
camera.position.set(camCenter.x, 220, camCenter.z + 154);

const controls = new OrbitControls(camera, canvas);
controls.target.copy(camCenter);
controls.enableDamping = true;
controls.dampingFactor = 0.08;
// Pitch is locked (min === max polar angle) AND azimuth/yaw rotation is disabled
// (controls.enableRotate = false below) — the perspective is fixed so the world
// always faces the same way, matching KTS and keeping orientation legible. Pan
// (middle-drag/arrows/edge-peek) and wheel-zoom remain live. FIXED_POLAR is the
// orbit polar angle measured from straight-up: SMALLER = more top-down. Args are
// atan2(horizontalOffset, cameraHeight), so the spawn offset above (z+154 over
// height 220) matches — keep them in sync if you tune one. History: atan2(28,24)≈49°
// → atan2(184,220)≈40° (2026-06-12) → atan2(154,220)≈35° polar = ~55° above the
// horizon, a touch more top-down (less tree/rock occlusion, LoL-ward) per Aviv's
// by-eye camera tuning, 2026-06-29.
const FIXED_POLAR = Math.atan2(154, 220);
// Default gameplay zoom (camera→target distance). Applied on spawn (see the
// camera.position.set near the match-start block, which scales this along
// FIXED_POLAR). Pulled in from the old ~37 — both the default AND the zoom-out
// cap read too far from the hero (playtest 2026-06-25).
const DEFAULT_CAM_DIST = 22;
// Locked view direction (azimuth). Like FIXED_POLAR it's enforced via a
// min===max clamp that controls.update() snaps to every frame. 0° = camera
// south of the hero looking north. The interim 78° (Aviv's 2026-07-02 by-eye
// pick) was taste on the old MIRRORED map, partly compensating for the flip;
// with the 2026-07-05 export-time N-S flip landed, 0° renders the map
// KTS-true (north up) and restores minimap↔screen direction agreement.
// Re-tunable by eye via the backtick debug panel's yaw slider.
const FIXED_YAW = THREE.MathUtils.degToRad(0);
controls.minPolarAngle = FIXED_POLAR;
controls.maxPolarAngle = FIXED_POLAR;
controls.minAzimuthAngle = FIXED_YAW;
controls.maxAzimuthAngle = FIXED_YAW;
controls.enableRotate = false; // lock the perspective — no yaw/azimuth spin (fixed MOBA view)
controls.enablePan = false; // right-click is click-to-move
controls.minDistance = 11; // closer max-zoom-in than the old 18 (playtest 2026-06-12)
controls.maxDistance = 30; // lowered from 45 — old cap framed the hero too small (playtest 2026-06-25)
// Reclaim the middle mouse button for our own drag-to-pan (see the middle-drag
// handler below). OrbitControls binds MIDDLE to dolly by default, which would
// fight the pan.
controls.mouseButtons.MIDDLE = null;
// Wheel-zoom is handled OURSELVES (see zoomTargetDist / applyZoomDamping below,
// 2026-07-02) instead of OrbitControls' built-in dolly: OrbitControls applies a
// wheel notch's full scale to the spherical radius in a single update() call —
// zero damping regardless of `enableDamping` (that flag only smooths rotation/
// pan; the zoom-radius line runs unconditionally) — which read as choppy,
// especially on fast scroll bursts or trackpads. `enableZoom = false` disables
// OrbitControls' own wheel listener entirely so it can't fight ours.
controls.enableZoom = false;
controls.update();

// MOBA-style camera with a peek/park split (2026-06-18, issue #5). Three behaviours:
//   • LOCKED (default): the frame loop smoothly keeps the local kobold centred.
//   • PEEK (edge-pan): brushing a screen edge nudges the view that way but does
//     NOT unlock — the moment the cursor leaves the edge it eases back to the
//     hero. This is almost always accidental (reaching for a resource near the
//     edge), so it self-heals; you can never get stranded by it.
//   • PARK (deliberate scout): arrow keys or a minimap click/drag unlock the
//     camera and leave it parked wherever you put it — for watching the far side
//     of the map — until you choose to return.
// Return to the hero anytime via Space or clicking your portrait (recenterOnHero).
// Zoom stays live in all modes; yaw-rotate is disabled (fixed perspective).
let cameraLocked = true;
// Master toggle for the whole-canopy occluder fade (camera-debug panel, so
// before/after is live-comparable). The screen-space hero-porthole circle
// this used to run alongside was DELETED 2026-07-02 — buildings never opted
// into it (the "backup for buildings" justification didn't hold up) and it
// stacked with this fade to re-punch holes through canopies the fade already
// handles. Whole-canopy fade is now the sole occlusion-readability mechanism.
let canopyFadeEnabledDebug = true;
// Tracks whether the canopy-fade drive condition (hero alive + world loaded)
// was open last frame — lets the frame loop detect the alive→dead / world-
// unload transition and snap any in-flight ghosted canopies back to solid
// instead of leaving them frozen mid-fade (bug fix 2026-07-02). See the
// canopyGateOpen usage in frame().
let canopyGateOpen = true;
// Camera/occlusion debug panel — created at match start, toggled with backtick (`).
let cameraDebug: CameraDebugPanel | null = null;
// Set true on frames where an edge-pan is nudging a still-locked camera; the
// follow loop skips its pull on those frames so the peek holds, then resumes
// (easing back to the hero) the moment the cursor leaves the edge.
let edgePeeking = false;
// Smoothly translate the look-at target to (tx,tz), moving the camera by the same
// delta so pitch/zoom/yaw are preserved (the lerped follow + spacebar share this).
//
// Optional `ty` lifts the look-at point to the followed unit's terrain height.
// The target's Y was previously pinned at 0 (ground plane at the map origin),
// but the player's mesh rides the heightmap (sampleHeight, ~0–23m of relief), so
// on a hill the camera aimed at a point BELOW the player — dragging the kobold
// down-screen, worst at max zoom where perspective magnifies the gap (it could
// slide under the bottom HUD). Lerping target.y to the ground height under the
// unit (and shifting the camera by the same dy) keeps the locked pitch + zoom
// distance unchanged while re-centring the unit vertically across all terrain.
// Below this remaining-offset (world units) a lerp step would just be chasing
// sub-pixel float noise from the predicted hero position forever (visible as a
// faint shimmer at rest). Snap the last hair instead of lerping it — this is a
// SNAP, not a deadzone: the target still converges to the exact hero position
// every frame, it just skips the asymptotic tail. Never lets the hero wander
// off-centre (2026-07-02, camera-follow refinement).
const PAN_SNAP_EPS = 0.01;
function panCameraTo(tx: number, tz: number, lerp: number, ty?: number): void {
  let dx = tx - controls.target.x;
  let dz = tz - controls.target.z;
  if (Math.hypot(dx, dz) >= PAN_SNAP_EPS) { dx *= lerp; dz *= lerp; }
  controls.target.x += dx; controls.target.z += dz;
  camera.position.x += dx; camera.position.z += dz;
  if (ty !== undefined) {
    let dy = ty - controls.target.y;
    if (Math.abs(dy) >= PAN_SNAP_EPS) dy *= lerp;
    controls.target.y += dy; camera.position.y += dy;
  }
}
// Frame-rate-independent exponential damping toward a target value: the
// shared formula behind both the follow-cam and wheel-zoom smoothing (one
// copy instead of two, 2026-07-02). `dt` in SECONDS, `tauMs` in MILLISECONDS.
// alpha = 1 - exp(-dt/tau) — see followAlpha's history below for why a raw
// per-frame blend factor was replaced with this.
function dampAlpha(dt: number, tauMs: number): number {
  return 1 - Math.exp(-dt / (tauMs / 1000));
}
// Frame-rate-independent follow damping (2026-07-02). The old CAM_FOLLOW_LERP
// (0.18) was a raw PER-FRAME blend factor, so a 144Hz player converged ~2.4×
// faster (wall-clock) than a 60Hz player, and any frame hitch produced a
// visible jerk (the "catching up" feel the owner explicitly ruled out).
// Replaced with exponential damping driven by real dt: alpha = 1 - exp(-dt/tau).
// tau is in MILLISECONDS and tunable live via the camera-debug panel ("Follow
// smoothing"); it persists to localStorage. Default reproduces the
// already-approved 0.18-at-60fps feel bit-for-bit (solve 0.18 = 1-exp(-dt/tau)
// at dt=1/60s ⇒ tau ≈ 84ms) so the immediate-but-refined MOBA feel that was
// already tuned by eye carries over unchanged — now consistent across frame
// rates and hitches. Nudge lower (tighter) from the debug panel if it should
// feel even snappier in a fight.
const DEFAULT_FOLLOW_TAU_MS = 84;
let followTauMs = DEFAULT_FOLLOW_TAU_MS;
function followAlpha(dt: number): number {
  return dampAlpha(dt, followTauMs);
}

// Wheel-zoom smoothing (2026-07-02, owner request: "zoom in and out is choppy
// and should be smoothed"). OrbitControls' built-in dolly applies a wheel
// notch's full scale to the camera distance in a single update() call — see
// `controls.enableZoom = false` above for why that reads as choppy. Instead,
// the wheel listener below only adjusts a TARGET distance (clamped to the
// same [minDistance, maxDistance] band OrbitControls used); applyZoomDamping()
// eases the camera's actual distance toward it every frame using the same
// dampAlpha() shape the follow-cam uses. tau starts snappier than the follow
// default (100ms vs 84ms follow) — zoom is a deliberate player input (unlike
// the ambient hero-follow pull), so it should feel refined but immediate, not
// floaty. Tunable live via the camera-debug panel ("Zoom smoothing").
const DEFAULT_ZOOM_TAU_MS = 100;
let zoomTauMs = DEFAULT_ZOOM_TAU_MS;
let zoomTargetDist = DEFAULT_CAM_DIST;
// Below this remaining-distance (world units) snap instead of easing — mirrors
// PAN_SNAP_EPS: converges to the exact target every frame, just skips the
// asymptotic tail so zoom never "keeps drifting" at rest.
const ZOOM_SNAP_EPS = 0.005;
const _zoomOffset = new THREE.Vector3(); // reused scratch: camera.position − controls.target
function applyZoomDamping(dt: number): void {
  _zoomOffset.copy(camera.position).sub(controls.target);
  const dist = _zoomOffset.length();
  if (dist < 1e-6) return; // degenerate (camera on top of target) — nothing to scale
  const diff = zoomTargetDist - dist;
  const nextDist = Math.abs(diff) < ZOOM_SNAP_EPS ? zoomTargetDist : dist + diff * dampAlpha(dt, zoomTauMs);
  _zoomOffset.setLength(nextDist);
  camera.position.copy(controls.target).add(_zoomOffset);
}
// Normalize a wheel event's deltaY the same way OrbitControls._customWheelEvent
// does (line-mode/page-mode scroll units, and the ctrlKey trackpad-pinch boost)
// so swapping to our own handler doesn't change how "big" a scroll/pinch feels.
function normalizedWheelDeltaY(e: WheelEvent): number {
  let d = e.deltaY;
  if (e.deltaMode === 1) d *= 16;       // LINE_MODE
  else if (e.deltaMode === 2) d *= 100; // PAGE_MODE
  if (e.ctrlKey) d *= 10;               // trackpad pinch-zoom gesture
  return d;
}
canvas.addEventListener("wheel", (e) => {
  e.preventDefault();
  const deltaY = normalizedWheelDeltaY(e);
  // Same falloff curve OrbitControls used internally (zoomSpeed=1 default):
  // pow(0.95, |delta|*0.01) — keeps the per-notch zoom increment feeling the
  // same as before, just applied to a smoothed target instead of instantly.
  const scale = Math.pow(0.95, Math.abs(deltaY) * 0.01);
  zoomTargetDist = THREE.MathUtils.clamp(
    deltaY < 0 ? zoomTargetDist * scale : zoomTargetDist / scale,
    controls.minDistance, controls.maxDistance,
  );
}, { passive: false });

// Vertical framing bias. The followed kobold is centred on the look-at target,
// but the bottom-centre HUD (ability bar) covers the lower strip, so a
// target-on-player leaves the kobold crammed against the HUD — worse when zoomed
// in (playtest 2026-06-12). We pull the look-at point back TOWARD the camera
// along its ground-forward axis (tracks yaw automatically), which renders the
// followed unit ABOVE screen centre, in the clear area between the top of the HUD
// and the top of the screen.
//
// The pull is a FRACTION of the current camera→target distance, not a fixed world
// length, so the on-screen lift stays constant across zoom. (A fixed world offset
// subtends a bigger angle when zoomed in, lifting the kobold further the closer
// you get — exactly the zoom-dependent inconsistency the playtest flagged.) The
// fraction is calibrated so that at the default max-zoom distance the kobold sits
// ~20% of a half-screen above centre (QA-verified NDC y ≈ +0.206), which keeps it
// roughly centred in the visible band above the HUD.
const CAM_VERTICAL_BIAS_FRAC = 4.0 / 45;
function followTarget(px: number, pz: number): { x: number; z: number } {
  const fx = controls.target.x - camera.position.x;
  const fy = controls.target.y - camera.position.y;
  const fz = controls.target.z - camera.position.z;
  const horizLen = Math.hypot(fx, fz) || 1;
  const dist = Math.hypot(fx, fy, fz);
  const bias = CAM_VERTICAL_BIAS_FRAC * dist;
  return { x: px - (fx / horizLen) * bias, z: pz - (fz / horizLen) * bias };
}

// Snap the camera back onto the local kobold AND re-engage follow-lock. Shared by
// the Space keybind and the click-on-portrait affordance (issue #5 recovery).
// Instant snap (lerp 1) with the same vertical framing bias + terrain-height lift
// as the follow loop so the recenter lands clean on slopes.
function recenterOnHero(): void {
  cameraLocked = true; // re-lock even if the player isn't loaded yet
  if (!room || !mySessionId) return;
  const cube = room.state.cubes.get(mySessionId);
  if (!cube) return;
  const t = followTarget(cube.x, cube.z);
  const gy = world ? world.sampleHeight(cube.x, cube.z) : undefined;
  panCameraTo(t.x, t.z, 1, gy);
}

(window as unknown as { __cam: (x: number, y: number, z: number, tx: number, tz: number) => void }).__cam =
  (x, y, z, tx, tz) => {
    camera.position.set(x, y, z);
    controls.target.set(tx, 0, tz);
    controls.update();
  };
(window as unknown as { __myCube: () => null | { x: number; z: number; targetX: number; targetZ: number; sessionId: string } }).__myCube =
  () => {
    if (!room || !mySessionId) return null;
    const c = room.state.cubes.get(mySessionId);
    if (!c) return null;
    return { x: c.x, z: c.z, targetX: c.targetX, targetZ: c.targetZ, sessionId: mySessionId };
  };
(window as unknown as { __send: (type: string, payload: unknown) => void }).__send = (type, payload) => {
  room?.send(type, payload as never);
};
// QA probe: the local hero's RENDERED world position (interpolated,
// INTERP_DELAY_MS behind now) next to the authoritative server position, so a
// journey can assert the renderer tracks the server (rendered trails by ~the
// interp delay while walking, no drift). null until the local mesh exists.
(window as unknown as {
  __renderPos: () => null | { rx: number; rz: number; sx: number; sz: number };
}).__renderPos = () => {
  if (!room || !mySessionId) return null;
  const obj = players.get(mySessionId)?.object;
  const c = room.state.cubes.get(mySessionId);
  if (!obj || !c) return null;
  return { rx: obj.position.x, rz: obj.position.z, sx: c.x, sz: c.z };
};
// QA move hook: mirrors the terrain right-click move branch (send moveTo AND
// clear the attack lock/pose) so a journey exercises the SAME path a right-click
// does — a bare __send("moveTo") never cancels the optimistic attack pose.
// (Pattern mirrors __qaOpenBuildingMenu.)
(window as unknown as { __qaMove: (x: number, z: number) => void }).__qaMove = (x, z) => {
  if (!room) return;
  room.send("moveTo", { x, z });
  chaseAttackId = null;
  if (mySessionId) { attackTargetById.delete(mySessionId); attackingUntil.delete(mySessionId); }
};
// QA hook exercising the REAL throttled click path (spam-protection test).
(window as unknown as { __qaMoveThrottled: (x: number, z: number) => void }).__qaMoveThrottled = (x, z) => {
  sendMoveThrottled(x, z);
};
// QA attack hook: mirrors the right-click attack branch (send attack AND arm
// the optimistic windup) so a journey exercises slice 2's real path. A bare
// __send("attack") only sends the server order and never arms the windup.
// (Pattern mirrors __qaMove.)
(window as unknown as { __qaAttack: (targetId: string) => void }).__qaAttack = (targetId) => {
  if (!room) return;
  room.send("attack", { targetId });
  optimisticAttack(targetId);
};
(window as unknown as { __players: () => Map<string, Player> }).__players = () => players;
// QA-only: per-creature mesh/material/animation facts for the asset-swap spotcheck.
// Read-only diagnostic (like __pickAt/__renderPos); returns [] until the render
// manager exists. Call twice with a wait to confirm animation via boneSample drift.
(window as unknown as { __inspectCreatures: () => unknown }).__inspectCreatures = () =>
  creatureRender ? creatureRender.inspect() : [];
(window as unknown as { __creatures: () => Array<{ id: string; type: string; x: number; z: number; targetX: number; targetZ: number; facing: number; hp: number; hpMax: number; aiState: string }> }).__creatures = () => {
  if (!room || !room.state.creatures) return [];
  const out: Array<{ id: string; type: string; x: number; z: number; targetX: number; targetZ: number; facing: number; hp: number; hpMax: number; aiState: string }> = [];
  room.state.creatures.forEach((c) => {
    out.push({ id: c.id, type: c.creatureType, x: c.x, z: c.z, targetX: c.targetX, targetZ: c.targetZ, facing: c.facing, hp: c.hp, hpMax: c.hpMax, aiState: c.aiState });
  });
  return out;
};
// Dev-only: inject one-way UPLINK latency (ms) for the local session (delays
// room.send) so command-to-response feel can be QA'd locally. RTT = 2×. Call
// __setNetLatency(0) to disable. No-op in prod (debug surface is dev-gated
// upstream anyway).
(window as unknown as { __setNetLatency: (ms: number) => string }).__setNetLatency = (ms) => {
  latencySim.set(ms);
  return `net latency = ${latencySim.latencyMs}ms one-way (RTT ${latencySim.latencyMs * 2}ms)`;
};
(window as unknown as {
  __nearestResource: (maxDist?: number) => null | { id: string; kind: string; x: number; z: number; dist: number; hp: number };
}).__nearestResource = (maxDist = 999) => {
  if (!room || !mySessionId) return null;
  const me = room.state.cubes.get(mySessionId);
  if (!me) return null;
  let best: { id: string; kind: string; x: number; z: number; dist: number; hp: number } | null = null;
  room.state.resources.forEach((node, id) => {
    const dx = node.x - me.x;
    const dz = node.z - me.z;
    const d = Math.hypot(dx, dz);
    if (d > maxDist) return;
    if (best === null || d < best.dist) {
      best = { id, kind: node.kind, x: node.x, z: node.z, dist: d, hp: node.hp };
    }
  });
  return best;
};

// Lighting per `docs/design/14-rendering.md` § "three-light setup". Day-preset values
// from `docs/superpowers/specs/2026-04-26-lighting-shaders-research.md` §1. Day/night
// crossfade is a future pass — these are the static "Day" keyframe.
// Primal re-grade DAY values — kept in sync with DAY in day-night-visual.ts
// (warm hazy sky fill + ochre ground bounce, was cool-blue 0x9bbed8/0x8c6a45).
const hemi = new THREE.HemisphereLight(0xe0c79c, 0xa8773e, 1.05);
scene.add(hemi);
const SUN_OFFSET = new THREE.Vector3(95, 50, 25);
// Shadow-map re-render cadence (frames). 2 = update every other frame; see the
// frame loop. Halves the shadow caster pass — the dominant GPU cost after foliage
// culling — at the price of a ≤1-frame lag on moving-unit shadows (imperceptible).
const SHADOW_UPDATE_INTERVAL = 2;
let shadowFrame = 0;
const sun = new THREE.DirectionalLight(0xffc176, 0.95);  // warm amber-gold key (Primal re-grade; sync w/ DAY)
sun.castShadow = true;
// Frustum half-size matches OrbitControls maxDistance (36u) plus slack so the camera
// can pan a bit before shadows pop. Repositioned per-frame to follow controls.target.
const SHADOW_HALF = 50;
sun.shadow.camera.left = -SHADOW_HALF;
sun.shadow.camera.right = SHADOW_HALF;
sun.shadow.camera.top = SHADOW_HALF;
sun.shadow.camera.bottom = -SHADOW_HALF;
sun.shadow.camera.near = 1;
sun.shadow.camera.far = 400;
sun.shadow.mapSize.set(2048, 2048);
sun.shadow.bias = -0.0005;
sun.shadow.normalBias = 0.04;
scene.add(sun);
scene.add(sun.target);

// Sky dome (issue #4) — replaces the flat grey background; recoloured each frame
// off the day/night lerp. (Terrain skirt + border trees handle the ground edge.)
const horizon = createHorizon({ scene, mapUnits: MAP_UNITS });

// Day/night lighting lerp + HUD clock cue. The visual mutates the static "Day"
// keyframe above toward a night keyframe based on the server cycle; the clock
// formats the same state into a phase indicator. Both are driven per frame.
const dayNightVisual: DayNightVisual = createDayNightVisual({ scene, sun, hemi, horizon });
const dayNightClock: DayNightClock = createDayNightClock();
const winReadout: WinReadout = createWinReadout();

addEventListener("resize", () => {
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(window.innerWidth, window.innerHeight);
  // Re-apply pixel ratio — dragging the window to a monitor with a different DPI
  // changes devicePixelRatio, and without this the canvas renders blurry/aliased
  // until reload. effectivePixelRatio() re-applies both the ≤2 hi-DPI clamp and
  // the player's resolution-scale dial.
  renderer.setPixelRatio(effectivePixelRatio());
});

let world: World | null = null;
let room: Room<HelloState> | null = null;
let mySessionId = "";
// Guards initGameClient against a double-build (e.g. a stray second
// matchState→"running" callback). Once the 3D scene + HUD + render loop are
// constructed for a room, further calls early-return.
let gameInited = false;
const players = new Map<string, Player>();

// Dev-only artificial uplink latency so movement feel can be QA'd locally
// (localhost ~0 ms hides it). Wired via window.__setNetLatency below.
const latencySim = new LatencySim();

// Debug dispatcher (`docs/design/13-debug.md`). Gated behind import.meta.env.DEV
// so Vite tree-shakes the entire `./debug/*` directory out of production bundles
// (`window.debug` is undefined in prod). Lazy getRoom/getMySessionId so install
// runs before connect; calls before connect reject with "not connected".
if (import.meta.env.DEV) {
  void import("./debug/install").then(({ installDebugApi, mountCheatMenu }) => {
    const api = installDebugApi({
      three: { scene, camera, renderer, controls, canvas },
      getRoom: () => room,
      getMySessionId: () => mySessionId,
      getTerrain: () => world?.terrain ?? null,
    });
    mountCheatMenu({
      api: api as unknown as Record<string, (...args: unknown[]) => unknown>,
      getMySessionId: () => mySessionId,
      // Resolves "players" / "creatureInstances" / etc. from live state at row-render time
      // so dropdowns reflect the current room. Static catalog sources (abilities, items,
      // buildings, etc.) are resolved internally from `@tribez/shared` registries.
      getDynamicEnum: (source) => {
        if (!room) return [];
        if (source === "players") {
          const out: { value: string; label: string }[] = [];
          room.state.cubes.forEach((c, sessionId) => {
            const tag = c.teamId === 99 ? "dummy"
              : c.teamId === 0 ? "T0"
              : c.teamId === 1 ? "T1"
              : `T${c.teamId}`;
            const me = sessionId === mySessionId ? " · me" : "";
            out.push({ value: sessionId, label: `${sessionId} (${tag}, ${c.state})${me}` });
          });
          return out;
        }
        if (source === "creatureInstances") {
          const out: { value: string; label: string }[] = [];
          room.state.creatures.forEach((c, id) => {
            out.push({ value: id, label: `${id} (${c.creatureType}, hp ${c.hp}/${c.hpMax})` });
          });
          return out;
        }
        return [];
      },
    });
  });
  // Direct scene/renderer handles for QA scripts. The `window.debug` Proxy hides
  // these (unknown property names get forwarded to the server), so QA tooling
  // can't reach them through the debug surface. Globals stay DEV-only.
  (window as unknown as { __scene: THREE.Scene; __renderer: THREE.WebGLRenderer; __sun: THREE.DirectionalLight }).__scene = scene;
  (window as unknown as { __renderer: THREE.WebGLRenderer }).__renderer = renderer;
  (window as unknown as { __sun: THREE.DirectionalLight }).__sun = sun;
  (window as unknown as { __camera: THREE.PerspectiveCamera }).__camera = camera;
  // Camera-follow QA probe (2026-07-02): the look-at target's world position +
  // the live tau, so a journey can sample the follow-damping settle curve
  // (teleport the hero, sample this every frame, assert exponential decay
  // with no overshoot) without reading OrbitControls internals directly.
  (window as unknown as { __camTarget: () => { x: number; y: number; z: number; followTauMs: number } }).__camTarget =
    () => ({ x: controls.target.x, y: controls.target.y, z: controls.target.z, followTauMs });
}

// When a player swings, hold their "attacking" action visible until this timestamp.
// Per-frame setAction(walking/idle) skips while now < holdUntil. 500ms gives a clear
// swing pose without overrunning the 1500ms cooldown.
const attackingUntil = new Map<string, number>();
// sourceId → its current attack targetId, set from the "swing" broadcast. Used to
// turn a stationary attacker to FACE its target (a swing/gather pose should look at
// the thing it's hitting, not keep the last walk heading — playtest 2026-06-12).
const attackTargetById = new Map<string, string>();

// Slice 2 — optimistic cosmetic action feedback. Play the local hero's windup
// pose the instant an attack/cast order is issued, BEFORE the server "swing" /
// "castResolved" broadcast confirms it (which on a hosted server is a full
// round-trip late). Reuses the same attacking-pose machinery the broadcast
// drives, so the server event seamlessly continues the pose; the server stays
// authoritative for all damage/effects/sounds. A misfire (rejected attack /
// fizzled cast) just plays a brief windup then returns to idle — harmless.
function playLocalWindup(targetId: string | undefined, holdMs: number): void {
  if (!mySessionId) return;
  const player = players.get(mySessionId);
  if (!player) return;
  if (targetId) attackTargetById.set(mySessionId, targetId);
  player.setAction("attacking");
  attackingUntil.set(mySessionId, Date.now() + holdMs);
}

// World position of any attackable entity id (cube / creature / building), for
// the optimistic-windup in-range gate.
function entityPos(id: string): { x: number; z: number } | null {
  if (!room) return null;
  const c = room.state.cubes?.get(id) ?? room.state.creatures?.get(id) ?? room.state.buildings?.get(id);
  return c ? { x: c.x, z: c.z } : null;
}

// Fire the optimistic attack windup only when the target is within ~melee reach
// of the rendered hero — out of range you're walking in (server-driven), where a
// swing pose would play against empty air.
const OPTIMISTIC_MELEE_RANGE = 3.5;
function optimisticAttack(targetId: string): void {
  const me = players.get(mySessionId)?.object.position;
  const tp = entityPos(targetId);
  if (!me || !tp) return;
  // Remember the target so the per-frame chase-windup (tickChaseWindup) can fire
  // the attack pose the instant the PREDICTED render arrives in range — otherwise,
  // when you attack something far away, the render leads to the target and idles
  // while the server's authoritative pos creeps the last bit before it swings
  // (~½s of dead idle = "runs too long, attack doesn't play right away").
  chaseAttackId = targetId;
  if (Math.hypot(tp.x - me.x, tp.z - me.z) <= OPTIMISTIC_MELEE_RANGE) {
    playLocalWindup(targetId, 500);
  }
  // Out of reach → the server chases; tickChaseWindup fires the pose the moment
  // the rendered hero arrives in range.
}

/** The local hero's current attack target while chasing it into range — set on an
 *  attack order, cleared on a move order or when the target is gone. Drives the
 *  optimistic chase-windup below. */
let chaseAttackId: string | null = null;
/** Per-frame: once the predicted render is within melee reach of the attack
 *  target and we're not already mid-swing, play the windup pose optimistically
 *  instead of idling — the server "swing" broadcast then seamlessly continues it. */
function tickChaseWindup(renderX: number, renderZ: number, moving: boolean): void {
  if (!chaseAttackId || !mySessionId) return;
  const tp = entityPos(chaseAttackId);
  if (!tp) { chaseAttackId = null; return; } // target gone
  if (moving) return; // still running in — wait until the render seats (like the gather pose)
  const hold = attackingUntil.get(mySessionId) ?? 0;
  if (hold > Date.now()) return; // already in a windup/swing pose
  if (Math.hypot(tp.x - renderX, tp.z - renderZ) <= OPTIMISTIC_MELEE_RANGE) {
    playLocalWindup(chaseAttackId, 500);
  }
}

// Spam protection for click-to-move. Each moveTo costs the server a findPath +
// a crowd retarget — spam-clicking floods both.
// Coalesce rapid clicks to ~1 per MOVE_THROTTLE_MS: the FIRST click of a burst
// fires immediately (leading edge, feels instant), further clicks within the
// window are dropped except the LAST, which fires on a trailing timer — so the
// hero always ends up heading where you last clicked, just not re-pathing 30×/s.
// (The server tick is 33ms, so >1 order/tick is wasted work anyway.)
const MOVE_THROTTLE_MS = 90;
let lastMoveSentAt = -1e9;
let pendingMove: { x: number; z: number } | null = null;
let pendingMoveTimer: ReturnType<typeof setTimeout> | null = null;
function doTerrainMove(x: number, z: number): void {
  if (!room) return;
  lastMoveSentAt = performance.now();
  room.send("moveTo", { x, z });
  chaseAttackId = null; // a move order cancels the attack (mirrors the server)
  // Moving cancels the (optimistic) attack pose so the walk anim shows immediately —
  // else a 500ms windup hold (re-armed by rapid attacks) masks the movement.
  if (mySessionId) { attackTargetById.delete(mySessionId); attackingUntil.delete(mySessionId); }
  attackTargetId = null;
  reticleTime = 0; // issuing a move clears the attack/harvest lock-on
  showIndicator(x, z);
}
function sendMoveThrottled(x: number, z: number): void {
  const since = performance.now() - lastMoveSentAt;
  if (since >= MOVE_THROTTLE_MS) {
    doTerrainMove(x, z);
    return;
  }
  pendingMove = { x, z }; // coalesce — keep only the latest
  if (pendingMoveTimer === null) {
    pendingMoveTimer = setTimeout(() => {
      pendingMoveTimer = null;
      if (pendingMove) { doTerrainMove(pendingMove.x, pendingMove.z); pendingMove = null; }
    }, MOVE_THROTTLE_MS - since);
  }
}

let resources: ResourceRenderer | null = null;
let hud: HudAPI | null = null;
let creatureRender: CreatureRenderManager | null = null;
// Whole-canopy occluder fade (feature 2) + a cached ref to the doodad "objects"
// group it raycasts against. Created once world load finishes (initGameClient).
let canopyFade: CanopyFadeController | null = null;
let doodadObjectsRoot: THREE.Object3D | null = null;
let projectileRender: ProjectileRenderManager | null = null;
let buildingRender: BuildingRenderManager | null = null;
let fog: FogApi | null = null;
let minimap: MinimapApi | null = null;
let placement: PlacementPreview | null = null;
let zmenu: ZMenu | null = null;
let menu: BuildingMenu | null = null;
let wonderHud: WonderHud | null = null;
let endScreen: EndScreen | null = null;
let mainPlate: MainPlate | null = null;
let petFrame: PetFrame | null = null;
let goldCounter: GoldCounter | null = null;
let helpPanel: HelpPanel | null = null;
let cookProgress: CookProgress | null = null;
// Task 7: stealth indicator pip (visible to local player while stealthed).
const stealthPip = (() => {
  const el = document.createElement("div");
  el.id = "stealth-pip";
  el.textContent = "STEALTHED";
  el.style.cssText = "position:fixed;top:110px;left:50%;transform:translateX(-50%);background:rgba(80,0,160,0.85);color:#d4b8ff;font:bold 13px/1 monospace;padding:4px 12px;border-radius:4px;pointer-events:none;display:none;z-index:100;";
  document.body.appendChild(el);
  return el;
})();
let expertiseArc: ExpertiseArc | null = null;
let offscreenHero: OffscreenHero | null = null;
let directionArrows: DirectionArrows | null = null;
let onboarding: Onboarding | null = null;
let floaterManager: FloaterManager | null = null;
let castVfx: CastVfxAPI | null = null;
// One-shot beam VFX spawned by pet-beam broadcasts (Nightwisp attacks).
// Ticked + culled each frame in the render loop alongside castVfx.
const petBeams: VfxInstance[] = [];
// One-shot burst VFX spawned by projectile-hit broadcasts.
// Ticked + culled each frame in the render loop.
const projectilePoofs: VfxInstance[] = [];
// Task 9: item-dash trail + arrival one-shot VFX (StagHuntersStiletto charge /
// ForestersMace leap). Spawned by tickDashTrailVfx (position-delta detector) in
// the cube loop; ticked + culled each frame alongside projectilePoofs.
const dashVfx: VfxInstance[] = [];
// Audio runtime (docs/design/18-audio.md). The manager loads lazily at boot
// (manifest fetch), stays silent until the landing "Play" gesture calls
// unlock(). Triggers + director are installed once a match room exists.
let audio: AudioManager | null = null;
let audioDirector: AudioDirectorAPI | null = null;
let audioGestured = false; // first user gesture seen → autoplay unlock allowed
// Settings panel (gear, top-right) — created at boot so volume controls are
// available on the menu as well as in-match. Reads/writes `audio` lazily.
let settingsPanel: SettingsPanel | null = null;

// Browsers gate audio until a user gesture, so menu music can't auto-start on
// the landing screen — it begins on the FIRST interaction anywhere (the
// earliest gesture available), then survives into the per-room lobby and is
// stopped by the director when a match starts. Idempotent: unlock()/loop() are
// safe to call repeatedly. If the click beats the manifest fetch, the boot
// `.then` below replays this once the manager exists.
function startMenuAudio(): void {
  if (!audio) return;
  audio.unlock();
  audio.loop("music.menu", { fadeMs: 1200 });
}
// Start on the FIRST interaction with the lobby browser — a click anywhere OR
// the first keystroke in the name field (typing a name / pressing Enter). The
// earlier pointerdown-only handler usually didn't fire until the click that
// *entered* a lobby, so music felt like it only began after joining.
function onFirstGesture(): void {
  audioGestured = true;
  startMenuAudio();
  window.removeEventListener("pointerdown", onFirstGesture, true);
  window.removeEventListener("keydown", onFirstGesture, true);
}
window.addEventListener("pointerdown", onFirstGesture, { capture: true });
window.addEventListener("keydown", onFirstGesture, { capture: true });

// Boot: fetch the manifest + construct the manager. Fire-and-forget — if it
// fails the game runs silent (a missing manifest is a no-op, not a crash).
void loadAudioManifest()
  .then((manifest) => {
    audio = new AudioManager(manifest);
    if (audioGestured) startMenuAudio(); // user already clicked while loading
  })
  .catch((err) => { console.warn("[audio] disabled — manifest load failed:", err); });

// Settings gear + modal (master mute + per-category volume sliders). Mounted at
// boot; reads the manager lazily so it works before the manifest resolves.
settingsPanel = createSettingsPanel(() => audio, () => renderer.setPixelRatio(effectivePixelRatio()));

// FPS / ping overlay (under the gold counter). Hidden unless the "Show FPS &
// ping" setting is on; the render loop feeds it sampled fps + the latest probe
// RTT. Mounted at boot so the toggle works from the menu onward.
const fpsOverlay = createFpsOverlay();
// Render-loop fps sampling (rolling ~0.5s window) + latest latency-probe RTT.
let fpsFrames = 0;
let fpsSampleStart = performance.now();
let latestPingMs: number | null = null;

let attackTargetId: string | null = null;
let craftMenu: CraftMenu | null = null;
let tavernShop: TavernShop | null = null;
let corpseLoot: CorpseLoot | null = null;
let corpseRender: CorpseRenderManager | null = null;
let castCursor: CastCursor | null = null;
// Any modal/menu surface that owns input right now (build shelf, contextual
// building menu, craft/tavern/corpse-loot panels, settings, help, or an armed
// placement preview). Single source of truth — composed once here and reused
// by the main-plate hotkey-suppression predicate AND the edge-pan HUD-guard
// (2026-07-02) so there's exactly one "is a menu open" signal in the codebase.
function isAnyModalOpen(): boolean {
  return Boolean(
    zmenu?.isOpen() || menu?.isOpen() || craftMenu?.isOpen() || tavernShop?.isOpen() ||
    corpseLoot?.isOpen() || settingsPanel?.isOpen() || helpPanel?.isOpen() || placement?.active(),
  );
}
// Themed cursor controller — swaps document.body.style.cursor by game context.
const cursorController = createCursorController();
// Latest building-placement validity (mirrors the value fed to placement.setValid).
let placementValid = false;
let abilityMarker: AbilityTargetMarker | null = null;
let hoverTooltip: HoverTooltip | null = null;
// Click-pinned unit inspect panel (bottom-left). `inspectTarget` is the selected
// unit; the render loop re-derives its content each frame from synced state so HP
// / gear / stance track live and it auto-closes when the target dies or leaves FoW.
let inspectPanel: InspectPanel | null = null;
let inspectTarget: { kind: "cube" | "creature"; id: string } | null = null;
// Hover-pick state. The raycast pick is the same cost as a right-click, so we
// only re-pick when the cursor moves or every ~6 frames (live HP/amount refresh);
// the cached content keeps the panel up on in-between frames.
let lastHoverContent: HoverContent | null = null;
let lastHoverX = -1;
let lastHoverY = -1;
let hoverFrameCounter = 0;
// Any mouse button held = camera drag / click in progress → suppress hover.
let mouseDown = false;

// Ground-ring helper. A flat ring placed at a single height slices through rising
// terrain (the far edge sinks below the slope and — with depthTest on — gets
// buried). These rings instead re-project every vertex onto the heightfield each
// frame so the circle hugs the ground. Geometry lies in the XZ plane (local
// y = 0); the mesh's position carries the center with y = 0, so per-vertex local
// y is the absolute terrain height + a small lift. depthTest stays ON (see the
// materials below) so creatures / trees / buildings correctly occlude the ring —
// it reads as painted on the ground, *under* whatever it targets.
const GROUND_RING_LIFT = 0.06;
type GroundRing = { mesh: THREE.Mesh; geo: THREE.BufferGeometry; baseXZ: Float32Array };

function makeGroundRing(name: string, innerR: number, outerR: number, seg: number, color: number): GroundRing {
  const geo = new THREE.RingGeometry(innerR, outerR, seg);
  geo.rotateX(-Math.PI / 2);
  const pos = geo.attributes.position as THREE.BufferAttribute;
  const baseXZ = new Float32Array(pos.count * 2);
  for (let i = 0; i < pos.count; i++) {
    baseXZ[i * 2] = pos.getX(i);
    baseXZ[i * 2 + 1] = pos.getZ(i);
  }
  const mesh = new THREE.Mesh(
    geo,
    new THREE.MeshBasicMaterial({
      color,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      // polygonOffset nudges the ring toward the camera so it doesn't z-fight the
      // terrain it hugs; depthTest left at its default (on).
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -1,
    }),
  );
  // Footprint changes as the ring grows/contracts — skip frustum culling so it
  // never pops at screen edges (one short-lived mesh; negligible cost).
  mesh.frustumCulled = false;
  mesh.visible = false;
  mesh.name = name; // QA hook — inspectTargetRings finds these by name
  scene.add(mesh);
  return { mesh, geo, baseXZ };
}

// Re-project `r`'s vertices onto the heightfield, scaled by `grow`, centered at
// (cx, cz). Sets the mesh visible; caller drives opacity.
function conformGroundRing(r: GroundRing, cx: number, cz: number, grow: number): void {
  if (!world) return;
  r.mesh.position.set(cx, 0, cz);
  const pos = r.geo.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    const bx = r.baseXZ[i * 2]! * grow;
    const bz = r.baseXZ[i * 2 + 1]! * grow;
    pos.setX(i, bx);
    pos.setZ(i, bz);
    pos.setY(i, world.sampleHeight(cx + bx, cz + bz) + GROUND_RING_LIFT);
  }
  pos.needsUpdate = true;
  r.mesh.visible = true;
}

// Move indicator: a green ring at the click destination, sized to about the
// kobold's footprint (~0.65u outer, ~half the old 1.4u disc) with a thin band so
// it reads as a tidy "you're going here" marker rather than a big yellow disc.
const indicatorRing = makeGroundRing("targetRing:move", 0.5, 0.65, 48, 0x44dd55);
const INDICATOR_DURATION = 0.7;
let indicatorTime = 0;
let indicatorX = 0;
let indicatorZ = 0;

function showIndicator(x: number, z: number) {
  if (!world) return;
  indicatorX = x;
  indicatorZ = z;
  indicatorTime = INDICATOR_DURATION;
}

// Unreachable-click cue: the server rejected a move/harvest order (no reachable
// ground near the click — "move-rejected"). A dull-red ring that double-blinks
// and contracts, visually the opposite of the green "you're going here" ring, so
// "can't go there" is legible instead of the old silent no-op (twice-flagged in
// playtests). Replaces the green indicator at the same spot (the optimistic
// right-click already drew one).
const rejectedRing = makeGroundRing("targetRing:rejected", 0.5, 0.68, 48, 0xd04038);
const REJECTED_DURATION = 0.55;
let rejectedTime = 0;
let rejectedX = 0;
let rejectedZ = 0;

function showRejected(x: number, z: number): void {
  if (!world) return;
  rejectedX = x;
  rejectedZ = z;
  rejectedTime = REJECTED_DURATION;
  indicatorTime = 0; // the click's green ring lied — replace it with the denial
  indicatorRing.mesh.visible = false;
}

// Targeting reticle — a red ring that "locks on" to whatever an attack/harvest
// right-click acknowledged (playtest 2026-06-12). It tracks the live target so a
// moving creature/player keeps the ring on its feet; a harvest node is static.
// Contracts + fades over RETICLE_DURATION, then disappears.
// Attack/lock-on reticle. Same outer footprint, but a thin band (−60% thickness:
// the old 0.45u band → 0.18u) so it reads as a crisp ring on the target's feet.
const attackReticle = makeGroundRing("targetRing:attack", 0.97, 1.15, 48, 0xff3b3b);
const RETICLE_DURATION = 1.2;
let reticleTime = 0;
let reticleTarget: { kind: "cube" | "creature" | "building"; id: string } | null = null;
let reticleStatic: { x: number; z: number } | null = null;

/** Lock the reticle onto a moving entity (player/creature/building). */
function showReticleOn(kind: "cube" | "creature" | "building", id: string): void {
  reticleTarget = { kind, id };
  reticleStatic = null;
  reticleTime = RETICLE_DURATION;
}
/** Place the reticle at a fixed world point (harvest node). */
function showReticleAt(x: number, z: number): void {
  reticleTarget = null;
  reticleStatic = { x, z };
  reticleTime = RETICLE_DURATION;
}

const raycaster = new THREE.Raycaster();
const ndc = new THREE.Vector2();

// Minimap view-box (issue #5): project the 4 viewport corners onto the look-at
// ground plane → the camera's world footprint, so the minimap can outline where
// you're currently looking vs. your hero. Returns null when a corner ray doesn't
// meet the plane (camera aimed at/above the horizon).
const _viewPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
const _viewHit = new THREE.Vector3();
const _VB_NDC: ReadonlyArray<readonly [number, number]> = [[-1, 1], [1, 1], [1, -1], [-1, -1]];
function computeViewBounds(): { x: number; z: number }[] | null {
  _viewPlane.constant = -controls.target.y; // horizontal plane at y = target.y
  const out: { x: number; z: number }[] = [];
  for (const [nx, ny] of _VB_NDC) {
    ndc.set(nx, ny);
    raycaster.setFromCamera(ndc, camera);
    if (!raycaster.ray.intersectPlane(_viewPlane, _viewHit)) return null;
    out.push({ x: _viewHit.x, z: _viewHit.z });
  }
  return out;
}

// ── game-QA probe hooks (dev-only, read-only) ────────────────────────────────
// Wrapped by qa/adapter.ts as probe.getPlayerAction / getPlayerScreen / getCutout.
// #1: local player's current animation action ("idle"|"walking"|"gathering"|...).
(window as unknown as { __playerAction: () => string | null }).__playerAction =
  () => (mySessionId ? players.get(mySessionId)?.getAction() ?? null : null);

// Arrange hook for the targeting-ring layering QA pass (2026-06-14). Calls the
// exact triggers the right-click handler uses — showIndicator (gold move ring),
// showReticleOn (red attack/lock-on reticle), showReticleAt (harvest-node
// reticle) — so QA exercises the real conform+depth render path. The journey
// runner has no raw-mouse step, so this is the input boundary of the system
// under test (the ring render/layering), NOT a bypass of it — same shape as
// arrange.castAbility triggering a real ability cast.
(window as unknown as {
  __qaTargeting: {
    move: (x: number, z: number) => void;
    attackOn: (kind: "cube" | "creature" | "building", id: string) => void;
    harvestAt: (x: number, z: number) => void;
    rejected: (x: number, z: number) => void;
    clear: () => void;
  };
}).__qaTargeting = {
  move: (x, z) => showIndicator(x, z),
  attackOn: (kind, id) => showReticleOn(kind, id),
  harvestAt: (x, z) => showReticleAt(x, z),
  rejected: (x, z) => showRejected(x, z),
  clear: () => {
    indicatorTime = 0;
    reticleTime = 0;
    rejectedTime = 0;
  },
};

// #6: local player projected to screen NDC + a centeredness metric (0 = dead
// center, ~1.41 = a corner). Lets QA assert the follow-cam keeps the player framed.
(window as unknown as {
  __playerScreen: () => null | { ndcX: number; ndcY: number; centerDist: number; onScreen: boolean };
}).__playerScreen = () => {
  if (!room || !mySessionId || !world) return null;
  const me = room.state.cubes.get(mySessionId);
  if (!me) return null;
  // Refresh the camera world matrix first — a minimap-jump / Space recenter moves
  // camera.position in the same tick BEFORE controls.update() runs, leaving
  // matrixWorldInverse stale and project() returning a degenerate NDC for one frame.
  camera.updateMatrixWorld();
  const v = new THREE.Vector3(me.x, world.sampleHeight(me.x, me.z) + 1.0, me.z).project(camera);
  return {
    ndcX: v.x, ndcY: v.y,
    centerDist: Math.hypot(v.x, v.y),
    onScreen: v.x >= -1 && v.x <= 1 && v.y >= -1 && v.y <= 1 && v.z < 1,
  };
};

// #7: occlusion-readability QA probe. Reports the whole-canopy fade's master
// enable state AND a CPU raycast camera→player against the doodad "objects"
// group, so QA can confirm a tree is genuinely between camera and player
// (occluderHit.closerThanPlayer) rather than guessing from the screenshot
// alone. Kept the `__cutoutProbe`/`getCutout` name (qa/adapter.ts) even though
// the screen-space hero-porthole "cutout" circle it originally reported on
// was deleted 2026-07-02 — this is generic occlusion QA, not circle-specific.
(window as unknown as { __cutoutProbe: () => unknown }).__cutoutProbe = () => {
  const out = {
    canopyFadeEnabled: canopyFadeUniforms.uCanopyFadeEnabled.value,
    occluderHit: { hit: false, distance: -1, closerThanPlayer: false } as
      { hit: boolean; distance: number; closerThanPlayer: boolean },
  };
  if (room && mySessionId && world) {
    const me = room.state.cubes.get(mySessionId);
    const objects = scene.getObjectByName("objects");
    if (me && objects) {
      const target = new THREE.Vector3(me.x, world.sampleHeight(me.x, me.z) + 1.0, me.z);
      const pDist = camera.position.distanceTo(target);
      const dir = target.clone().sub(camera.position).normalize();
      const rc = new THREE.Raycaster(camera.position.clone(), dir, 0.1, pDist + 5);
      const occ = rc.intersectObject(objects, true).find((h) => h.distance < pDist - 0.5);
      out.occluderHit = { hit: !!occ, distance: occ ? occ.distance : -1, closerThanPlayer: !!occ };
    }
  }
  return out;
};

// Cast cursor — armed-cursor state machine for Q/W/E/R abilities. Created
// here (not inside the async init) so the keydown handlers below can capture
// it as a closure variable; the lazy getRoom/getCube getters defer until
// after the connect resolves. Visual feedback: crosshair cursor while armed.
let emptyAbilityToastShown = false; // once-per-match: teach the Tavern on first empty cast
castCursor = createCastCursor({
  getCube: () => {
    if (!room || !mySessionId) return null;
    return room.state.cubes.get(mySessionId) ?? null;
  },
  getRoom: () => room,
  onArmedChange: (armed) => {
    // Cursor is owned by the themed cursorController (cast state); just toggle the marker.
    if (armed) abilityMarker?.show(armed);
    else abilityMarker?.hide();
  },
  onEmptySlot: (slot) => {
    // E doubles as the craft/loot/Tavern key when empty, so it gives its own
    // feedback (a menu opens) — only Q/W/R fizzle silently. Teach once per match.
    if (slot === "E") return;
    if (emptyAbilityToastShown) return;
    emptyAbilityToastShown = true;
    flashToast("Buy spells at a Tavern — they fill Q/W/E/R.");
  },
  onNoTarget: () => flashToast("No valid target — aim at a unit."),
  onCast: () => {
    // Optimistic cast windup (slice 2): play the caster's windup pose the instant
    // the cast is dispatched, so casting feels responsive under latency. Gate on
    // the hero being stationary — an out-of-range cast triggers a server
    // walk-to-range, and a windup pose while sliding looks wrong; in that case the
    // server's cast event drives the pose on arrival. No target id (facing holds).
    if (!room || !mySessionId) return;
    const me = room.state.cubes.get(mySessionId);
    if (!me) return;
    const moving = Math.hypot(me.targetX - me.x, me.targetZ - me.z) > 0.05;
    if (!moving) playLocalWindup(undefined, 400);
  },
});

function pickPlayer(raycaster: THREE.Raycaster): string | null {
  // Each Player owns an Object3D root with a hierarchy. Test rays against root → find
  // the player whose object contains the closest hit. We iterate players because
  // sessionId isn't stamped on meshes.
  let best: { sessionId: string; dist: number } | null = null;
  for (const [sessionId, player] of players) {
    if (sessionId === mySessionId) continue;  // can't click self
    // Raycast the invisible pick proxy (a body-enclosing cylinder), NOT the thin
    // character mesh — the silhouette is too small/gappy to click reliably on the
    // zoomed-out camera (playtest 2026-06-30). See player.ts PICK_* constants.
    const hits = raycaster.intersectObject(player.pickProxy, false);
    if (hits.length === 0) continue;
    const d = hits[0]!.distance;
    if (!best || d < best.dist) {
      best = { sessionId, dist: d };
    }
  }
  return best?.sessionId ?? null;
}

// Resolve the entity under the cursor into a hover-tooltip content descriptor.
// Same pick priority as right-click (player > creature > building > resource);
// own hero is intentionally not hoverable (pickPlayer skips self). Returns null
// when nothing hoverable is under the cursor.
function computeHover(): HoverContent | null {
  if (!world || !room || !mySessionId || mouseClientX < 0) return null;
  const me = room.state.cubes?.get(mySessionId); // @view map can be undefined pre-hydration
  const rect = canvas.getBoundingClientRect();
  ndc.x = ((mouseClientX - rect.left) / rect.width) * 2 - 1;
  ndc.y = -((mouseClientY - rect.top) / rect.height) * 2 + 1;
  raycaster.setFromCamera(ndc, camera);

  const pid = pickPlayer(raycaster);
  if (pid) {
    const c = room.state.cubes.get(pid);
    if (c) {
      const rel: HoverRelation =
        c.teamId === 99 ? "neutral"
        : me && c.teamId === me.teamId ? "ally"
        : "enemy";
      return cubeHover(pid, c.meta?.name ?? "", c.isBot, c.expertise?.characterLevel ?? 1, c.hp, c.hpMax, rel);
    }
  }

  if (creatureRender) {
    const cid = creatureRender.pickCreature(raycaster);
    if (cid) {
      const cr = room.state.creatures.get(cid);
      if (cr && cr.hp > 0) {
        // Own/ally pets (a creature WITH an owner on your team) hover friendly —
        // no attack cursor — so they read as selectable, not a target.
        const friendlyPet = cr.ownerId !== "" && !!me && cr.teamId === me.teamId;
        return creatureHover(cid, cr.creatureType, cr.hp, cr.hpMax, friendlyPet);
      }
    }
  }

  if (buildingRender) {
    const bid = buildingRender.pickBuilding(raycaster);
    if (bid) {
      const b = room.state.buildings.get(bid);
      if (b && b.state !== "destroyed") {
        const rel: HoverRelation =
          me && b.teamId === me.teamId ? (b.ownerId === mySessionId ? "own" : "ally") : "enemy";
        const ownerName = b.ownerId ? room.state.cubes.get(b.ownerId)?.meta?.name ?? "" : "";
        return buildingHover(bid, b.buildingType, b.hp, b.hpMax, rel, ownerName);
      }
    }
  }

  if (resources) {
    const node = resources.pickResource(raycaster);
    if (node) {
      const n = room.state.resources.get(node.id);
      // Only show the harvest cursor over the TRUNK — match the right-click gate
      // so the canopy reads as walk-through, not harvestable (Aviv 2026-06-30).
      if (n) {
        const groundY = world ? world.sampleHeight(n.x, n.z) : node.point.y;
        if (isHarvestTrunkHit(node.kind, node.point, n.x, n.z, groundY))
          return resourceHover(node.id, n.kind, n.hp, n.hpMax);
      }
    }
  }

  return null;
}

// Resolve the themed cursor for this frame. Priority: building placement (build
// vs invalid) → armed ability cast → whatever the last hover pick decided
// (attack / harvest-* baked into HoverContent.cursor) → default. Placement and
// cast are checked first because the hover pick is suppressed during them.
function resolveCursorState(): CursorState {
  if (placement?.active()) return placementValid ? "build" : "invalid";
  if (castCursor?.armed()) return "cast";
  return lastHoverContent?.cursor ?? "default";
}

// Per-frame refresh of the click-pinned inspect panel. Re-derives the selected
// unit's content from synced state so HP / gear / stance stay live, and auto-closes
// (clearing `inspectTarget`) when the target dies, is removed, or leaves fog-of-war
// (its cube/creature drops out of the client's StateView → `get` returns undefined).
// Client-only — same synced-state read path as computeHover().
function refreshInspect(): void {
  if (!inspectPanel) return;
  const me = room && mySessionId ? room.state.cubes.get(mySessionId) : null;
  // Nothing selected, or a hard blocker (no world/room, local player dead, match over).
  if (!inspectTarget || !world || !room || !me || me.state === "dead" || room.state.matchState === "ended") {
    if (inspectTarget && (!me || me.state === "dead" || room?.state.matchState === "ended")) inspectTarget = null;
    inspectPanel.update(null);
    return;
  }

  if (inspectTarget.kind === "cube") {
    const c = room.state.cubes.get(inspectTarget.id);
    if (!c || c.state === "dead") { inspectTarget = null; inspectPanel.update(null); return; }
    const relation: InspectRelation =
      c.teamId === 99 ? "neutral" : c.teamId === me.teamId ? "ally" : "enemy";
    inspectPanel.update(inspectCube(
      inspectTarget.id,
      c.meta?.name ?? "",
      c.expertise?.characterLevel ?? 1,
      c.hp, c.hpMax, relation,
      {
        "main-hand": c.equipment?.get("main-hand")?.itemId ?? "",
        offhand: c.equipment?.get("offhand")?.itemId ?? "",
        armor: c.equipment?.get("armor")?.itemId ?? "",
      },
    ));
  } else {
    const cr = room.state.creatures.get(inspectTarget.id);
    if (!cr || cr.hp <= 0 || cr.ownerId === "") { inspectTarget = null; inspectPanel.update(null); return; }
    const relation: InspectRelation =
      cr.teamId === 99 ? "neutral" : cr.teamId === me.teamId ? "ally" : "enemy";
    const owner = cr.ownerId === mySessionId ? "You" : (room.state.cubes.get(cr.ownerId)?.meta?.name || "Ally");
    inspectPanel.update(inspectPet(
      inspectTarget.id, cr.creatureType, cr.level, cr.hp, cr.hpMax, relation, owner, cr.posture,
    ));
  }
}

// Harvest height-gate: a right-click on a resource only harvests when the hit
// landed within this many world-units above the node's base — i.e. on the low
// part standing on the ground. Higher hits (branches, canopy) fall through to a
// move so you can navigate behind tall trees. Tunable.
//
// The gate MUST scale with the mesh's actual on-screen height, or a tall node
// becomes un-harvestable: the ElderTree mesh is ~13m tall (21.6m native × 0.60
// baseScale in resources.ts), so almost every click on its trunk/canopy lands
// far above a flat 3.0m gate → the harvest is never sent and the click falls
// through to a terrain move. That reads as "harvesting sometimes starts but
// doesn't credit" (you actually just walked toward the tree). Per-kind ceilings
// sized to ~80% of each tree's rendered height let a click anywhere on the trunk
// harvest while a click high in the canopy still navigates; short nodes (bushes,
// deposits) sit entirely under the default and harvest on any click as before.
const HARVEST_MAX_HIT_HEIGHT = 3.0;
const HARVEST_MAX_HIT_HEIGHT_BY_KIND: Record<string, number> = {
  // Generous trunk column below the canopy crown — high enough that the whole
  // visible trunk harvests, but still under the spreading canopy so you can
  // click past the leaves to move (Aviv 2026-06-30: trunk-only was too punishing).
  ElderTree: 9.0,
  MapleTree: 5.5,
};
// Horizontal trunk radius (world-units from the node centre) a harvest hit must
// land within. Restricts harvest to the STEM column and not the far leaves: the
// canopy spreads much wider than this, so wide/drooping-leaf hits fall through to
// a move. Sized generously so the trunk is an easy target. Unlisted kinds
// (bushes, stone & gold deposits) have no radius gate — harvest on any click.
const HARVEST_MAX_HIT_RADIUS_BY_KIND: Record<string, number> = {
  ElderTree: 3.0,
  MapleTree: 2.2,
};
/** True when a resource raycast hit is on the harvestable TRUNK — low enough
 *  (below the canopy) AND close enough to the node centre (the stem, not the
 *  leaves). Shared by the hover cursor and the right-click harvest so both agree
 *  on where a tree is harvestable. `cx/cz` is the node's world centre. */
function isHarvestTrunkHit(kind: string, point: THREE.Vector3, cx: number, cz: number, groundY: number): boolean {
  const maxHit = HARVEST_MAX_HIT_HEIGHT_BY_KIND[kind] ?? HARVEST_MAX_HIT_HEIGHT;
  if (point.y - groundY > maxHit) return false;
  const maxR = HARVEST_MAX_HIT_RADIUS_BY_KIND[kind];
  if (maxR === undefined) return true; // short nodes: no horizontal gate
  const dx = point.x - cx, dz = point.z - cz;
  return dx * dx + dz * dz <= maxR * maxR;
}

// Dismiss any open building-interact menu (craft shelf, building action menu,
// tavern spell shop, corpse loot) — called when the player issues a move/command
// or clicks away, so a menu never lingers while you walk off (Aviv 2026-06-30).
// The Z build menu + HUD panels (help/settings) are intentionally excluded —
// they own their own toggles.
function closeInteractMenus(): void {
  if (menu?.isOpen()) menu.close();
  if (craftMenu?.isOpen()) craftMenu.close();
  if (tavernShop?.isOpen()) tavernShop.close();
  if (corpseLoot?.isOpen()) corpseLoot.close();
}

// Close EVERY menu — the Z build menu plus the interact menus. Used to enforce
// one-menu-at-a-time when opening any of them (otherwise the build menu + a craft
// menu can both be open and fight over Tab/number/E/Esc keys — Aviv 2026-06-30),
// and to dismiss them all on a move/command/click-away.
function closeAllMenus(): void {
  if (zmenu?.isOpen()) zmenu.close();
  closeInteractMenus();
}

function rightClickAt(clientX: number, clientY: number): void {
  if (placement && placement.active()) {
    // right-click cancels placement
    placement.cancel();
    return;
  }
  if (!world || !room) return;
  if (mySessionId && room.state.cubes.get(mySessionId)?.state === "dead") return;
  // Issuing a move/attack/harvest command dismisses any open menu (build + interact).
  closeAllMenus();

  const rect = canvas.getBoundingClientRect();
  ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
  ndc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
  raycaster.setFromCamera(ndc, camera);

  // Priority: enemy player > creature > enemy building > resource > terrain.
  // Clicking your team's player ignored (we don't have follow/group commands at MVP).
  // Clicking your team's building falls through to resource/terrain — Task 14
  // (building menu) replaces this with a left-click open-menu interaction.
  const targetId = pickPlayer(raycaster);
  if (targetId) {
    const target = room.state.cubes.get(targetId);
    const me = mySessionId ? room.state.cubes.get(mySessionId) : null;
    if (target && me && target.teamId !== me.teamId && target.state === "alive") {
      room.send("attack", { targetId });
      optimisticAttack(targetId);
      attackTargetId = targetId;
      showReticleOn("cube", targetId);
      return;
    }
  }

  if (creatureRender) {
    const creatureId = creatureRender.pickCreature(raycaster);
    if (creatureId) {
      const creature = room.state.creatures.get(creatureId);
      const me = mySessionId ? room.state.cubes.get(mySessionId) : null;
      // Own/ally pets are not attack targets — skip the whole creature branch so
      // the click falls through to terrain/corpse. This both stops the attack
      // reticle on your own pet AND lets you pick up loot a pet is standing over
      // (the pet no longer intercepts the click). Wild + enemy creatures below.
      const ownOrAllyPet = !!creature && creature.ownerId !== "" && !!me && creature.teamId === me.teamId;
      if (creature && creature.hp > 0 && !ownOrAllyPet) {
        // Tame gesture: wild tameable species + player holds a tame item.
        const mainHandId = me?.equipment?.get("main-hand")?.itemId ?? "";
        const armorId    = me?.equipment?.get("armor")?.itemId ?? "";
        const hasTameItem = mainHandId === "ObedienceRod" || armorId === "MasterRangersMaille";
        if (hasTameItem && creature.ownerId === "" && isPetSpecies(creature.creatureType)) {
          room.send("tame", { targetId: creatureId });
          showReticleOn("creature", creatureId);  // targeting feedback (same red reticle as attack)
          return;
        }
        room.send("attack", { targetId: creatureId });
        optimisticAttack(creatureId);
        attackTargetId = creatureId;
        showReticleOn("creature", creatureId);
        return;
      }
    }
  }

  if (buildingRender) {
    const buildingId = buildingRender.pickBuilding(raycaster);
    if (buildingId) {
      const b = room.state.buildings.get(buildingId);
      const me = mySessionId ? room.state.cubes.get(mySessionId) : null;
      if (b && b.state !== "destroyed" && me) {
        if (b.teamId !== me.teamId) {
          room.send("attack", { targetId: buildingId });
          optimisticAttack(buildingId);
          showReticleOn("building", buildingId);
          return;
        }
        // own building → quick-salvage shortcut. Allies (same team but
        // different owner) fall through to resource/terrain.
        if (b.ownerId === mySessionId) {
          room.send("salvage", { id: buildingId });
          return;
        }
      }
    }
  }

  if (resources) {
    const node = resources.pickResource(raycaster);
    if (node) {
      const n = room.state.resources.get(node.id);
      // Height-gate harvest vs. move. Only the LOW base of a node (the part
      // standing on the ground) harvests; clicking higher up — a tall Elder
      // trunk, branches, canopy — occludes the ground behind it, so it falls
      // through to a terrain move and you walk past the tree instead of harvesting
      // it. Short nodes (bushes, stone/gold deposits) sit entirely below the
      // threshold, so they harvest on any click as before.
      const groundY = n ? world.sampleHeight(n.x, n.z) : node.point.y;
      const cx = n ? n.x : node.point.x, cz = n ? n.z : node.point.z;
      if (isHarvestTrunkHit(node.kind, node.point, cx, cz, groundY)) {
        room.send("harvest", { id: node.id });
        chaseAttackId = null; // harvest cancels the attack
        if (n) showReticleAt(n.x, n.z);
        return;
      }
      // Canopy / off-trunk hit → fall through to the terrain move below (navigate behind it).
    }
  }

  const hits = raycaster.intersectObject(world.terrain, false);
  if (hits.length === 0) return;
  const p = hits[0]!.point;

  // Right-click on a dropped pile / corpse → loot it, no panel (grab it the way
  // you'd grab any ground item). The loot beacon isn't raycast-pickable, so match
  // the terrain click to the nearest corpse base. In reach → loot every slot at
  // once; out of reach → walk to it. (E still opens the panel for selective loot.)
  const meRC = mySessionId ? room.state.cubes.get(mySessionId) : null;
  if (meRC) {
    const CORPSE_CLICK_RADIUS = 1.1; // terrain-click distance that counts as "clicking the pile" — tight so a click just outside the prop doesn't grab it
    const LOOT_REACH = 2.5;          // server PICKUP_RADIUS — in reach loots now, else walk over
    const near = findNearbyCorpse({ x: p.x, z: p.z }, room.state.corpses.values(), CORPSE_CLICK_RADIUS);
    const corpse = near ? room.state.corpses.get(near.id) : undefined;
    if (corpse) {
      const rm = room; // capture for the forEach closure (module `let room` loses narrowing inside it)
      if (Math.hypot(corpse.x - meRC.x, corpse.z - meRC.z) <= LOOT_REACH) {
        if (corpse.gold > 0) rm.send("pickup", { corpseId: corpse.id, slot: "gold" });
        corpse.items.forEach((s, key) => {
          if (s.itemId !== "" && s.count > 0) rm.send("pickup", { corpseId: corpse.id, slot: key });
        });
      } else {
        rm.send("moveTo", { x: corpse.x, z: corpse.z });
      }
      attackTargetId = null;
      reticleTime = 0;
      return;
    }
  }

  // Throttled: coalesces spam-clicks (a server findPath + crowd retarget per
  // click) to ~1 per MOVE_THROTTLE_MS while always honoring the latest target.
  // First click of a burst fires immediately, so deliberate clicks feel instant.
  sendMoveThrottled(p.x, p.z);
}

// Commands fire on right-button PRESS, not on `contextmenu`: browsers dispatch
// contextmenu after the button is RELEASED, so routing move/harvest/attack
// through it taxed every command with the physical click-hold time (~40-70ms —
// 2026-07-10 latency diagnosis). RTS/MOBA convention is button-down. The right
// button is otherwise unbound (camera rotate/pan disabled; middle = drag-pan).
canvas.addEventListener("pointerdown", (e) => {
  if (e.button !== 2) return;
  // If a cast cursor is armed, right-click cancels instead of moving.
  if (castCursor?.armed()) {
    castCursor.cancel();
    return;
  }
  rightClickAt(e.clientX, e.clientY);
});
// The canvas contextmenu (fires later, on release) only suppresses the browser
// menu — the command already went out on pointerdown above.
canvas.addEventListener("contextmenu", (e) => e.preventDefault());

// Global context-menu suppression. The canvas handler above owns right-click as
// click-to-move; this catches right-clicks that land on HUD overlays or the
// viewport edges so the native browser menu never interrupts play. Bubbles after
// the canvas listener (idempotent preventDefault); for non-canvas targets it's
// the only handler that fires.
addEventListener("contextmenu", (e) => e.preventDefault());

// Left-click while a cast cursor is armed: raycast the target, dispatch
// `castIntent`, and stop propagation so placement-confirm /
// building-menu-open don't double-handle the click. Registered BEFORE the
// other left-click handlers (which live in the async init) so it intercepts
// first.
canvas.addEventListener("click", (e) => {
  if (!castCursor || !castCursor.armed()) return;
  if (!world || !room) return;
  e.preventDefault();
  e.stopImmediatePropagation();

  const rect = canvas.getBoundingClientRect();
  ndc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
  ndc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
  raycaster.setFromCamera(ndc, camera);

  // Unit pick priority (mirrors right-click attack priority): player >
  // creature > building > resource. The server's validateTarget will
  // reject filter-mismatches; client just dispatches.
  let hitUnitId: string | undefined;
  const playerId = pickPlayer(raycaster);
  if (playerId) {
    hitUnitId = playerId;
  } else if (creatureRender) {
    const cId = creatureRender.pickCreature(raycaster);
    if (cId) hitUnitId = cId;
  }
  if (!hitUnitId && buildingRender) {
    const bId = buildingRender.pickBuilding(raycaster);
    if (bId) hitUnitId = bId;
  }
  if (!hitUnitId && resources) {
    const node = resources.pickResource(raycaster);
    if (node) hitUnitId = node.id;
  }

  // Ground point — used for point/point-charge modes; for unit mode we
  // pass it but worldClick discards it (reads hitUnitId only).
  const hits = raycaster.intersectObject(world.terrain, false);
  const point = hits[0]?.point ?? null;
  if (!point && !hitUnitId) {
    // Off-map click with no unit hit: cancel the cast.
    castCursor.cancel();
    return;
  }

  castCursor.worldClick(
    point ? { x: point.x, z: point.z } : { x: 0, z: 0 },
    hitUnitId,
  );
});

// Placement preview: mousemove updates the cursor footprint outline + tints it
// green/red based on local range check (3.0u from player). Server is still the
// authority; this is just UX feedback.
canvas.addEventListener("mousemove", (e) => {
  if (!placement || !placement.active() || !world || !room || !mySessionId) return;
  const rect = canvas.getBoundingClientRect();
  ndc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
  ndc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
  raycaster.setFromCamera(ndc, camera);
  const p = placement.hover(raycaster, world.terrain, world.sampleHeight);
  if (!p) return;
  const me = room.state.cubes.get(mySessionId);
  if (!me) return;
  const inRange = Math.hypot(p.x - me.x, p.z - me.z) <= 3.0;
  placement.setValid(inRange);
  placementValid = inRange;
});

// Left-click confirms placement when active. Registered before any later
// menu/open-handlers so it short-circuits the click while a building is being
// placed.
canvas.addEventListener("click", (e) => {
  if (!placement || !placement.active() || !room) return;
  e.preventDefault();
  e.stopPropagation();
  const out = placement.confirm();
  if (out) room.send("build", { type: out.type, x: out.x, z: out.z });
});

// Left-click a unit (enemy/ally player or a pet) pins the inspect panel bottom-left.
// Registered after placement-confirm, before the building-menu handler: a click on a
// unit selects it (and stops propagation so the building handler doesn't closeAllMenus);
// a click hitting no inspectable unit clears the selection and falls through so the
// building/close handler still runs. Right-click is untouched (it still commands).
canvas.addEventListener("click", (e) => {
  if (castCursor?.armed() || placement?.active()) return; // those handlers own this click
  if (!world || !room || !mySessionId) return;
  const rect = canvas.getBoundingClientRect();
  ndc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
  ndc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
  raycaster.setFromCamera(ndc, camera);

  // Player cube first (mirrors pick priority). pickPlayer skips self, so you can't
  // inspect your own hero — matches the locked scope (enemy + ally + pets, not self).
  const pid = pickPlayer(raycaster);
  if (pid && room.state.cubes.get(pid)) {
    inspectTarget = { kind: "cube", id: pid };
    e.stopImmediatePropagation();
    return;
  }
  // Then pets — a creature WITH an owner. Wild / enemy non-pet creatures are not
  // inspectable in v1 (they keep the hover tooltip).
  if (creatureRender) {
    const cid = creatureRender.pickCreature(raycaster);
    if (cid) {
      const cr = room.state.creatures.get(cid);
      if (cr && cr.hp > 0 && cr.ownerId !== "") {
        inspectTarget = { kind: "creature", id: cid };
        e.stopImmediatePropagation();
        return;
      }
    }
  }
  // No inspectable unit under the cursor → deselect (fall through, no stop).
  inspectTarget = null;
});

// Left-click on an allied building opens the per-building action menu. Runs
// only when placement is NOT active (placement listener short-circuits above
// — but `stopPropagation` doesn't stop sibling listeners on the same element,
// so we re-guard here). Clicking empty space closes any open menu.
// Build the building-menu context from the viewer's POV. StorageOutpost trade
// rows read gold + per-resource holdings to gray out un-affordable / empty /
// lock-blocked actions; other buildings ignore the trade fields.
function menuOpts(b: Building, me: Cube): MenuOpts {
  const hold: Record<string, number> = {};
  for (const k of TRADE_RESOURCES) hold[k] = getMaterial(me, k);
  return {
    isOwner: b.ownerId === mySessionId,
    inRange: Math.hypot(b.x - me.x, b.z - me.z) <= 6.0,
    isEnemy: b.teamId !== me.teamId,
    trade: { gold: me.resources.gold, hold },
  };
}

// Human-readable toast for a fired building-action. Trade actions + lock toggle
// get friendly text ("Sold Lumber (+12g)"); everything else echoes the raw id.
function tradeActionToast(action: string): string {
  if (action === "locked") return "Storage locked";
  if (action === "unlocked") return "Storage unlocked";
  const t = parseTradeAction(action);
  if (!t) return action;
  const p = TRADE_PRICES[t.key];
  return t.side === "sell" ? `Sold ${p.label} (+${p.sell}g)` : `Bought ${p.label} (-${p.buy}g)`;
}
/** Resource label for a sell/buy action (reject toasts), or "resource" fallback. */
function parseTradeActionLabel(action: string): string {
  const t = parseTradeAction(action);
  return t ? TRADE_PRICES[t.key].label.toLowerCase() : "resource";
}

canvas.addEventListener("click", (e) => {
  if (placement?.active()) return;
  if (!world || !room || !mySessionId || !buildingRender || !menu) return;
  const rect = canvas.getBoundingClientRect();
  ndc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
  ndc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
  raycaster.setFromCamera(ndc, camera);
  const bId = buildingRender.pickBuilding(raycaster);
  if (!bId) { closeAllMenus(); return; }
  const b = room.state.buildings.get(bId);
  const me = room.state.cubes.get(mySessionId);
  if (!b || !me || b.state === "destroyed") { closeAllMenus(); return; }
  // Allied buildings open the menu; enemy buildings stay right-click-attackable —
  // EXCEPT StorageOutpost, which is a cross-tribe market (buy/sell at any outpost,
  // LockStorage gates non-owner buys). Left-click opens its trade menu either way.
  if (b.teamId !== me.teamId && b.buildingType !== "StorageOutpost") {
    closeAllMenus(); return;
  }
  const isOwner = b.ownerId === mySessionId;
  // Forge / Workbench / Hunter's Lodge skip the action menu and open the craft
  // shelf directly (Salvage lives inside the shelf now). The Lodge opens the
  // unified menu (Craft + Pet Mastery + Trophies tabs). Campfire keeps its
  // action menu — it has fire controls (light/douse/torch) the shelf lacks.
  const bt = b.buildingType as BuildingType;
  // Every building interaction closes whatever else is open first, so only ONE
  // menu (build or interact) is ever up at a time (Aviv 2026-06-30).
  if (bt === "Forge" || bt === "Workbench" || bt === "HuntersLodge") {
    closeAllMenus();
    craftMenu?.open(bt, { buildingId: b.id, isOwner, bx: b.x, bz: b.z });
    return;
  }
  // Tavern opens its spell shop directly (no intermediate action menu) — the
  // owner's Salvage moves into the shop footer, mirroring the craft shelf.
  if (bt === "Tavern") {
    closeAllMenus();
    tavernShop?.open({ buildingId: b.id, isOwner });
    return;
  }
  closeAllMenus();
  menu.open(b, menuOpts(b, me));
});

// Esc cancels placement and closes the Z menu / building menu if open.
addEventListener("keydown", (e) => {
  if (e.key === "Escape") {
    if (placement?.active()) placement.cancel();
    if (zmenu?.isOpen()) zmenu.close();
    if (menu?.isOpen()) menu.close();
    if (craftMenu?.isOpen()) craftMenu.close();
    if (tavernShop?.isOpen()) tavernShop.close();
    if (corpseLoot?.isOpen()) corpseLoot.close();
    if (helpPanel?.isOpen()) helpPanel.close();
    if (settingsPanel?.isOpen()) settingsPanel.close();
    castCursor?.cancel();
    inspectTarget = null; // dismiss the pinned inspect panel
  }
});

// Spacebar recenters the camera on the local player AND re-engages follow-lock
// (RTS/MOBA convention). Same path as clicking the portrait → recenterOnHero.
addEventListener("keydown", (e) => {
  if (e.code !== "Space") return;
  // Don't steal the spacebar when a text input has focus.
  const tag = (document.activeElement?.tagName ?? "").toLowerCase();
  if (tag === "input" || tag === "textarea") return;
  e.preventDefault();
  recenterOnHero();
});

// Q/W/E/R ability keybindings. pressSlot is a no-op when the slot is empty,
// so empty slots fall through to existing handlers (E → craft menu). Self-
// targeted abilities cast immediately; point/unit-targeted abilities arm the
// cursor for the next left-click.
//
// Conflict notes:
//   - WASD camera pan was removed in favor of mouse-edge pan + spacebar
//     recenter, so Q/W/E/R have no movement conflicts.
//   - E shares with the contextual craft/corpse menu handler below; that
//     handler now skips when an E ability is bound.
addEventListener("keydown", (e) => {
  if (e.repeat) return;
  // Let browser/OS chords through (Ctrl+W close, Ctrl+R reload, Alt+Tab, ⌘+…).
  // Without this an ability fires AND the browser shortcut runs.
  if (e.ctrlKey || e.altKey || e.metaKey) return;
  if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
  if (!castCursor) return;
  // While placement / build-menu / contextual menus are open, hotkeys belong
  // to those flows (build menu uses Tab + 1-9). Suppress ability casts
  // until they're dismissed. For E specifically, this hand-off lets the
  // contextual handler below CLOSE the open menu first (close-before-cast).
  if (placement?.active() || zmenu?.isOpen()) return;
  if (craftMenu?.isOpen() || corpseLoot?.isOpen() || tavernShop?.isOpen() ||
      menu?.isOpen() || settingsPanel?.isOpen() || helpPanel?.isOpen()) return;
  const k = e.key.toLowerCase();
  if (k === "q") castCursor.pressSlot("Q");
  else if (k === "w") castCursor.pressSlot("W");
  else if (k === "e") castCursor.pressSlot("E");
  else if (k === "r") castCursor.pressSlot("R");
});

// V-key walk toggle. Sends "walk-toggle" to the server which flips
// cube.survival.walkToggled and adds/removes the WalkToggled transient
// behavior (× 0.6 movement multiplier). e.repeat guard prevents key-hold spam.
addEventListener("keydown", (e) => {
  if (e.code === "KeyV") {
    if (e.repeat) return;
    if (e.ctrlKey || e.altKey || e.metaKey) return; // don't hijack Ctrl/⌘+V paste
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    if (!room) return;
    room.send("walk-toggle");
  }
});

// Backtick (`) toggles the camera/occlusion debug panel (pitch / FOV / cutout).
addEventListener("keydown", (e) => {
  if (e.code !== "Backquote") return;
  if (e.repeat) return;
  if (e.ctrlKey || e.altKey || e.metaKey) return;
  if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
  cameraDebug?.toggle();
});

// F/H pet-posture keybinds. F = Follow (auto-engage), H = Stay (hold position).
// Guards mirror the V-key handler: no repeat, no browser chords, no text inputs,
// no room guard (silently dropped if not in a match).
addEventListener("keydown", (e) => {
  if (e.repeat) return;
  if (e.ctrlKey || e.altKey || e.metaKey) return;
  if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
  if (!room) return;
  if (e.code === "KeyF") { room.send("pet-posture", { posture: "Follow" }); }
  else if (e.code === "KeyH") { room.send("pet-posture", { posture: "Stay" }); }
});

// G — heal-bear active (Bear Shaman's Shroud). Scans the caller's own live Bears,
// picks the lowest-HP one, and sends "heal-bear" with its targetId. Server validates
// Shroud + cooldown; replies with "heal-bear-cooldown" if not yet ready.
addEventListener("keydown", (e) => {
  if (e.repeat) return;
  if (e.ctrlKey || e.altKey || e.metaKey) return;
  if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
  if (!room || e.code !== "KeyG") return;
  if (!mySessionId) return;

  // Find the caller's own live Bear with the lowest HP (best heal target).
  // (id/hp tracked as plain locals; TS can't narrow a closure-assigned object
  // through the outer `if`, so flat primitives sidestep a type cast.)
  let bestBearId = "";
  let bestBearHp = Infinity;
  room.state.creatures?.forEach((c) => {
    if (c.ownerId === mySessionId && c.creatureType === "Bear" && c.hp > 0 && c.hp < bestBearHp) {
      bestBearId = c.id;
      bestBearHp = c.hp;
    }
  });
  if (bestBearId) {
    room.send("heal-bear", { targetId: bestBearId });
    return;
  }

  // Task 6: G-key also triggers DreadplateArmor AoE fear when equipped in armor slot.
  // If no Bear heal was sent, check for Dreadplate in armor slot and fire the active.
  const me = room.state.cubes.get(mySessionId);
  if (!me) return;
  if (me.equipment?.get("armor")?.itemId === "DreadplateArmor") {
    room.send("use-dreadplate");
    return;
  }
  // Task 8: G-key also triggers WispMothersWrappings armor active.
  // Sacrifices an owned Nightwisp within 7u, spawns a GuardianSpirit replacement.
  if (me.equipment?.get("armor")?.itemId === "WispMothersWrappings") {
    room.send("use-wisp-mother");
    return;
  }
  // Task 7: armor-slot stealth cloaks (PhantomCape, LurkersMantle) are self-targeted
  // actives in ITEM_SELF_MSG. The C/X handlers only read main-hand/offhand, so an
  // armor-slot self-active is only reachable here on G. (Caught by game-qa: PhantomCape
  // was unreachable until this fallback was added.)
  const armorSelfMsg = ITEM_SELF_MSG[me.equipment?.get("armor")?.itemId ?? ""];
  if (armorSelfMsg) {
    room.send(armorSelfMsg);
    return;
  }
  // No toast if no Bear, Dreadplate, WispMother, or armor self-active — silent per existing G-key convention.
});

// Maps item behaviorId → the server message for its slot active.
// Extensible: add a row here whenever a new item active is built.
const ITEM_ACTIVE_MSG: Record<string, string> = {
  ObedienceRod:    "use-bait",
  HuntingMachete:  "use-machete",
  // BearShamansShroud → "heal-bear" is handled by the G key above; listed here
  // for documentation only — C/X resolver doesn't need to duplicate it.
  // Task 6: CC item actives wired to C/X key (equipment slot active).
  // ReapersScythe — silence a targeted enemy; sends targetId like use-machete.
  // ReapersScythe is a main-hand/offhand weapon, so the C/X resolver reaches it.
  ReapersScythe:   "use-reapers-scythe",
  // Task 9: StagHuntersStiletto — main-hand charge to a targeted enemy. Sends
  // { targetId } like use-reapers-scythe (resolved via resolveEnemyTarget).
  StagHuntersStiletto: "use-stiletto",
  // DreadplateArmor is ARMOR-slot only — the C/X resolver queries main-hand /
  // offhand, never armor, so it must NOT live here (it'd be a dead entry). Its
  // active is reachable via the G-key fall-through above (after the bear-heal
  // check), mirroring BearShamansShroud's G-key path.
};

// Task 7: Stealth items are SELF-targeted — no enemy target needed.
// C/X key checks this map first and sends immediately with empty payload.
// Task 8: summon items are also self-targeted (spawn at the caster's feet).
const ITEM_SELF_MSG: Record<string, string> = {
  PhantomCape:             "use-phantom-cape",
  LurkersMantle:           "use-lurkers-mantle",
  TalonsofthePilferer:     "use-talons-cloak",
  // Task 8: equipment slot summons — self-targeted (no enemy target needed).
  // Demonward: off-hand → X key fires "use-demonward".
  // AbysmalClaw: main-hand or off-hand → C/X key fires "use-abysmal-claw".
  Demonward:               "use-demonward",
  AbysmalClaw:             "use-abysmal-claw",
  // Task 9: ForestersMace — main-hand self-active leap to the nearest tree
  // (server resolves the tree; no target payload needed). C key → empty msg.
  ForestersMace:           "use-foresters-mace",
};


// Task 7: client-side stealth buff detection for local translucency + pip.
const STEALTH_BUFF_IDS = new Set(["PhantomCapeCloak", "LurkersMantleCloak", "TalonsCloak", "OracleSlayerCloak", "StripedLurkerCloak", "PowderedFlaskCloak"]);
function hasStealthBuff(cube: import("@tribez/shared").Cube): boolean {
  let found = false;
  cube.spellbook.buffs.forEach((_inst: unknown, id: string) => {
    if (STEALTH_BUFF_IDS.has(id)) found = true;
  });
  return found;
}

/** Return the nearest live enemy cube to the given player position (falls back
 *  to null if none exist). Used as a fallback when attackTargetId is unset or
 *  no longer valid. */
function nearestEnemyCube(myX: number, myZ: number, myTeam: number): string | null {
  if (!room) return null;
  let bestId: string | null = null;
  let bestDist = Infinity;
  room.state.cubes.forEach((c, id) => {
    if (c.teamId === myTeam || c.teamId === 99 || c.state !== "alive") return;
    const dx = c.x - myX, dz = c.z - myZ;
    const dist = Math.sqrt(dx * dx + dz * dz);
    if (dist < bestDist) { bestDist = dist; bestId = id; }
  });
  return bestId;
}

/** Return the nearest live attackable creature to the given player position
 *  (wild creatures + enemy pets), or null. Mirrors nearestEnemyCube but over
 *  state.creatures — lets creature-targeting item actives (e.g. the Stiletto
 *  dash) fall back to a nearby beast when no enemy cube is closer. */
function nearestEnemyCreature(myX: number, myZ: number, myTeam: number): { id: string; dist: number } | null {
  if (!room) return null;
  let bestId: string | null = null;
  let bestDist = Infinity;
  room.state.creatures.forEach((c, id) => {
    if (c.hp <= 0) return;
    // Skip own/allied pets (ownerId set + same team); wild + enemy pets are fair game.
    if (c.ownerId !== "" && c.teamId === myTeam) return;
    const dx = c.x - myX, dz = c.z - myZ;
    const dist = Math.sqrt(dx * dx + dz * dz);
    if (dist < bestDist) { bestDist = dist; bestId = id; }
  });
  return bestId ? { id: bestId, dist: bestDist } : null;
}

/** Resolve the best enemy target for an item active: prefer current attackTargetId
 *  if it is a live non-neutral enemy CUBE *or* a live CREATURE (right-clicking a
 *  wolf sets attackTargetId to its creature id); otherwise fall back to nearest
 *  enemy cube, then nearest attackable creature. */
function resolveEnemyTarget(me: { x: number; z: number; teamId: number }): string | null {
  if (!room) return null;
  if (attackTargetId) {
    const t = room.state.cubes.get(attackTargetId);
    if (t && t.teamId !== me.teamId && t.teamId !== 99 && t.state === "alive") {
      return attackTargetId;
    }
    // The right-clicked target may be a CREATURE (not a cube) — honor it so a
    // dash/charge fires at a targeted beast. Server-side handlers resolve
    // creature ids for these actives.
    const c = room.state.creatures.get(attackTargetId);
    if (c && c.hp > 0 && !(c.ownerId !== "" && c.teamId === me.teamId)) {
      return attackTargetId;
    }
  }
  const cubeId = nearestEnemyCube(me.x, me.z, me.teamId);
  if (cubeId) return cubeId;
  return nearestEnemyCreature(me.x, me.z, me.teamId)?.id ?? null;
}

// C — main-hand slot active. Reads the item in cube.equipment["main-hand"] and
// sends the matching server message (e.g. HuntingMachete → "use-machete").
// Guards mirror the V/F/H/G handlers exactly.
addEventListener("keydown", (e) => {
  if (e.repeat) return;
  if (e.ctrlKey || e.altKey || e.metaKey) return;
  if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
  if (!room || e.code !== "KeyC") return;
  if (!mySessionId) return;
  const me = room.state.cubes.get(mySessionId);
  if (!me) return;
  const itemId = me.equipment?.get("main-hand")?.itemId ?? "";
  // Task 7: stealth items are self-targeted; check ITEM_SELF_MSG first.
  const selfMsgC = ITEM_SELF_MSG[itemId];
  if (selfMsgC) { room.send(selfMsgC, {}); return; }
  const msg = ITEM_ACTIVE_MSG[itemId];
  if (!msg) return; // no active on this slot — silent
  const targetId = resolveEnemyTarget(me);
  if (targetId) room.send(msg, { targetId });
});

// X — off-hand slot active. Mirrors C-key handler for the offhand slot.
addEventListener("keydown", (e) => {
  if (e.repeat) return;
  if (e.ctrlKey || e.altKey || e.metaKey) return;
  if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
  if (!room || e.code !== "KeyX") return;
  if (!mySessionId) return;
  const me = room.state.cubes.get(mySessionId);
  if (!me) return;
  const itemId = me.equipment?.get("offhand")?.itemId ?? "";
  // Task 7: stealth items are self-targeted; check ITEM_SELF_MSG first.
  const selfMsgX = ITEM_SELF_MSG[itemId];
  if (selfMsgX) { room.send(selfMsgX, {}); return; }
  const msg = ITEM_ACTIVE_MSG[itemId];
  if (!msg) return; // no active on this slot — silent
  const targetId = resolveEnemyTarget(me);
  if (targetId) room.send(msg, { targetId });
});

function flashToast(msg: string) {
  const t = document.createElement("div");
  t.className = "toast";
  t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => t.remove(), 2200);
}

// Center-screen level-up banner. lastSelfLevel starts at -1 so the FIRST observed
// level (on spawn / reconnect) only primes the tracker — the banner fires only on
// a genuine increase, never on initial sync.
let lastSelfLevel = -1;
function flashLevelUp(level: number) {
  const t = document.createElement("div");
  t.className = "levelup-banner";
  t.innerHTML = `<span class="levelup-banner__kicker">Level Up</span><span class="levelup-banner__lvl">${level}</span>`;
  document.body.appendChild(t);
  void t.offsetWidth;
  t.classList.add("show");
  setTimeout(() => { t.classList.remove("show"); setTimeout(() => t.remove(), 400); }, 1800);
}

// Z toggles the build menu. While open: Tab (Shift+Tab) switches category and
// the number keys 1-9 pick the nth building in the active tab — Q/W/E stay free
// for abilities. Registered separately so it doesn't conflict with the Esc
// handler or the camera-pan / ability-cast keydown listeners above.
addEventListener("keydown", (e) => {
  // Don't hijack typing or browser chords (Ctrl+Z undo, ⌘+Z, etc.).
  if (e.ctrlKey || e.altKey || e.metaKey) return;
  if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
  if (e.key === "z" || e.key === "Z") {
    if (zmenu && !zmenu.isOpen()) closeInteractMenus(); // opening build menu closes interact menus
    zmenu?.toggle();
    return;
  }
  if (zmenu?.isOpen()) {
    if (e.key === "Tab") { e.preventDefault(); zmenu.cycleTab(e.shiftKey ? -1 : 1); return; }
    const n = Number(e.key);
    if (Number.isInteger(n) && n >= 1 && n <= 9) { zmenu.selectHotkey(n); return; }
    return;
  }
  // Craft menu shares the build-menu controls: Tab switches the slot tab, 1-9
  // craft the nth tile. Other keys (E / Esc) fall through to their handlers so
  // the menu still closes.
  if (craftMenu?.isOpen()) {
    if (e.key === "Tab") { e.preventDefault(); craftMenu.cycleTab(e.shiftKey ? -1 : 1); return; }
    const n = Number(e.key);
    if (Number.isInteger(n) && n >= 1 && n <= 9) { craftMenu.selectHotkey(n); return; }
  }
  // E-key contextual menu (Phase 7 D2). Priority: corpse loot > craft. Toggles
  // closed if the relevant menu is already open. Suppressed while z-menu is
  // open (handled above) so E still places a Wonder during build mode.
  if (e.key === "e" || e.key === "E") {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    if (!room || !mySessionId) return;
    const me = room.state.cubes.get(mySessionId);
    if (!me) return;

    // Close-before-cast: if a contextual menu is open, E closes it FIRST — even
    // when an ability is bound to E (the cast handler above is suppressed while a
    // menu is open, so E won't also cast underneath).
    if (zmenu?.isOpen()) { zmenu.close(); return; }
    if (corpseLoot?.isOpen()) { corpseLoot.close(); return; }
    if (craftMenu?.isOpen()) { craftMenu.close(); return; }
    if (tavernShop?.isOpen()) { tavernShop.close(); return; }

    // No menu open: if an ability is bound to E, the cast-cursor handler owns the
    // keypress — skip the open-menu fallback so E doesn't double-fire.
    if (me.spellbook.slotE) return;

    // Priority 1: corpse loot — closer than craft, gameplay-time-sensitive
    const corpseNear = findNearbyCorpse(me, room.state.corpses.values());
    if (corpseNear) { corpseLoot?.open(corpseNear.id); return; }

    // Priority 2: craft menu near friendly Forge / Workbench / Campfire (cooking)
    const nearby = findNearbyCraftBuilding(me, room.state.buildings.values());
    if (nearby) {
      const nb = room.state.buildings.get(nearby.id);
      craftMenu?.open(nearby.type, nb ? { buildingId: nb.id, isOwner: nb.ownerId === mySessionId, bx: nb.x, bz: nb.z } : undefined);
      return;
    }

    // Priority 3: spell shop near a friendly Tavern
    if (findNearbyTavern(me, room.state.buildings.values())) { tavernShop?.open(); return; }
    // Priority 4 (Lodge mastery panel) removed: Lodge is now Priority 2 via
    // findNearbyCraftBuilding, which opens the unified craft menu (Task 7).
  }
});

(window as unknown as { __pickAt: (x: number, y: number) => unknown }).__pickAt = (x, y) => {
  if (!world || !resources) return null;
  const rect = canvas.getBoundingClientRect();
  ndc.x = ((x - rect.left) / rect.width) * 2 - 1;
  ndc.y = -((y - rect.top) / rect.height) * 2 + 1;
  raycaster.setFromCamera(ndc, camera);
  const node = resources.pickResource(raycaster);
  const terrainHits = raycaster.intersectObject(world.terrain, false);
  return {
    node,
    terrain: terrainHits[0] ? { x: terrainHits[0].point.x, y: terrainHits[0].point.y, z: terrainHits[0].point.z } : null,
    cam: { x: camera.position.x, y: camera.position.y, z: camera.position.z },
  };
};

// QA-only world→screen projector (dev). Mirrors __pickAt's NDC↔pixel math so a
// harvest-pick diagnostic can target a RESOURCE NODE's screen position (not the
// player's), independent of camera-follow framing. Returns canvas pixel coords +
// onScreen flag, or null pre-boot. Read-only; same category as __pickAt/__playerScreen.
(window as unknown as {
  __worldToScreen: (x: number, y: number, z: number) => null | { px: number; py: number; ndcX: number; ndcY: number; onScreen: boolean };
}).__worldToScreen = (x, y, z) => {
  if (!world) return null;
  camera.updateMatrixWorld();
  const v = new THREE.Vector3(x, y, z).project(camera);
  const rect = canvas.getBoundingClientRect();
  return {
    px: Math.round(rect.left + ((v.x + 1) / 2) * rect.width),
    py: Math.round(rect.top + ((1 - v.y) / 2) * rect.height),
    ndcX: v.x, ndcY: v.y,
    onScreen: v.x >= -1 && v.x <= 1 && v.y >= -1 && v.y <= 1 && v.z < 1,
  };
};

(window as unknown as { __spawnDummy: (x?: number, z?: number, hp?: number) => void }).__spawnDummy =
  (x, z, hp) => { room?.send("__spawnDummy", { x, z, hp }); };
(window as unknown as { __attackId: (id: string) => void }).__attackId =
  (id) => { room?.send("attack", { targetId: id }); };
(window as unknown as { __heal: () => void }).__heal =
  () => { room?.send("__heal", {}); };
(window as unknown as { __kill: () => void }).__kill =
  () => { room?.send("__kill", {}); };
(window as unknown as { __respawn: () => void }).__respawn =
  () => { room?.send("__respawn", {}); };
(window as unknown as { __myHp: () => null | { hp: number; hpMax: number; state: string; respawnAt: number; teamId: number } }).__myHp =
  () => {
    if (!room || !mySessionId) return null;
    const c = room.state.cubes.get(mySessionId);
    if (!c) return null;
    return { hp: c.hp, hpMax: c.hpMax, state: c.state, respawnAt: c.respawnAt, teamId: c.teamId };
  };
(window as unknown as { __teamId: () => number | null }).__teamId =
  () => {
    if (!room || !mySessionId) return null;
    return room.state.cubes.get(mySessionId)?.teamId ?? null;
  };

// Build the 3D scene + HUD + render loop for a JOINED, RUNNING room. Called by
// the menu controller (enterGame) once a room is/becomes "running" — never at
// module load, so booting to the landing menu costs no three.js work. The
// connection itself is owned by the menu bootstrap below; `joinedRoom` is the
// already-joined room object (reused so its server-side StateView survives).
async function initGameClient(joinedRoom: Room<HelloState>): Promise<void> {
  if (gameInited) return;
  gameInited = true;
  room = joinedRoom;
  mySessionId = room.sessionId;
  (window as unknown as { __room: typeof room }).__room = room;
  // Install the dev latency uplink delay (no-op until __setNetLatency sets > 0).
  // DEV-only so production's room.send path is never wrapped (the predictor
  // itself always runs; only the QA latency injector is gated out of prod).
  if (import.meta.env.DEV) {
    latencySim.attach(room as unknown as { send: (type: string, message?: unknown) => void });
  }
  statusEl.textContent = `connected (${mySessionId.slice(0, 4)})`;
  statusEl.classList.add("connected");

  try {
    world = await loadWorld(scene);
  } catch (err) {
    statusEl.textContent = `world load failed: ${(err as Error).message}`;
    console.error(err);
    loadingScreen.finish(); // don't strand the player behind the overlay
    return;
  }
  // World assets are in — the big await is done; snap the bar near full while the
  // renderers + room subscriptions wire up below (cheap, synchronous-ish).
  loadingScreen.milestone(0.92);

  resources = createResourceRenderer(world.assets, world.sampleHeight);
  scene.add(resources.group);

  creatureRender = createCreatureRender(scene, world.assets, world.sampleHeight);
  // Doodad "objects" group (world/doodads.ts buildObjects) — named lookup, same
  // target the __cutoutProbe QA hook already raycasts against. Cached once so
  // the per-frame canopy-fade scan doesn't re-walk the scene graph.
  doodadObjectsRoot = scene.getObjectByName("objects") ?? null;
  canopyFade = createCanopyFadeController();
  projectileRender = createProjectileRender(scene, world.sampleHeight, () => room?.state.cubes.get(mySessionId)?.teamId ?? 0);
  buildingRender = createBuildingRender(scene, world.assets, world.sampleHeight);
  // Fog of war shroud — covers the play mesh AND the edge skirt (issue #4) so the
  // shroud is continuous past the boundary; driven each frame from the local
  // team's vision sources (shared/vision.ts, same math the server gates with).
  const skirtGeom = (world.terrain.children.find((c) => c.name === "terrain-skirt") as THREE.Mesh | undefined)?.geometry;
  fog = createFog({
    scene,
    terrainGeometry: world.terrain.geometry,
    extraGeometries: skirtGeom ? [skirtGeom] : [],
    sampleHeight: world.sampleHeight,
  });
  // Minimap — top-right; mirrors fog state + entities; click-to-pan the camera.
  minimap = createMinimap({
    getState: () => room?.state ?? null,
    getMySessionId: () => mySessionId,
    getFog: () => fog,
    getViewBounds: () => computeViewBounds(),
    onJumpTo: (x, z) => {
      // Jumping to a minimap location is a DELIBERATE scout → park (free-look
      // until recenter). Shift camera + target by the same delta so zoom/angle
      // are preserved (mirrors the pan model).
      if (cameraLocked) flashToast("Free-look — press Space to snap back to your hero");
      cameraLocked = false;
      const dx = x - controls.target.x;
      const dz = z - controls.target.z;
      controls.target.x += dx;
      controls.target.z += dz;
      camera.position.x += dx;
      camera.position.z += dz;
      controls.update();
    },
  });
  placement = createPlacementPreview(scene);
  abilityMarker = createAbilityTargetMarker(scene);
  zmenu = createZMenu();
  zmenu.onSelect((type) => {
    zmenu!.close();
    placement?.start(type);
  });

  menu = createBuildingMenu();
  menu.onAction(({ buildingId, action }) => {
    if (!room) return;
    if (action === "salvage") {
      room.send("salvage", { id: buildingId });
      return;
    }
    if (action === "openCraft") {
      // Building-menu shortcut to the Phase 7 craft menu — same surface E
      // opens, but reachable via left-click on Forge/Workbench/Campfire too.
      // Campfire is the primary path now that E is consumed by the ability slot.
      const b = room.state.buildings.get(buildingId);
      if (!b) return;
      const t = b.buildingType as "Forge" | "Workbench" | "Campfire";
      if (t !== "Forge" && t !== "Workbench" && t !== "Campfire") return;
      menu?.close();
      craftMenu?.open(t, { buildingId: b.id, isOwner: b.ownerId === mySessionId, bx: b.x, bz: b.z });
      return;
    }
    if (action === "openShop") {
      // Building-menu shortcut to the tavern spell shop — same surface E opens
      // near a friendly Tavern, but reachable via left-click on the Tavern too.
      const b = room.state.buildings.get(buildingId);
      if (!b || b.buildingType !== "Tavern") return;
      menu?.close();
      tavernShop?.open();
      return;
    }
    room.send("building-action", { id: buildingId, action });
  });

  // QA hook: open the building menu for a building id, mirroring the canvas
  // left-click path (no DOM target exists on a 3D building, so Playwright can't
  // click it directly). Once open, the menu's `.bld-menu__act[data-act=...]`
  // rows ARE real DOM and can be clicked authentically. Dev-surface only,
  // alongside __qaTargeting / __pickAt. Returns true if the menu opened.
  (window as unknown as { __qaOpenBuildingMenu: (id: string) => boolean }).__qaOpenBuildingMenu = (id) => {
    if (!room || !mySessionId || !menu) return false;
    const b = room.state.buildings.get(id);
    const me = room.state.cubes.get(mySessionId);
    if (!b || !me || b.state === "destroyed") return false;
    if (b.teamId !== me.teamId && b.buildingType !== "StorageOutpost") return false;
    menu.open(b, menuOpts(b, me));
    return true;
  };

  wonderHud = createWonderHud();
  // Play Again leaves the match (consented) and reloads to the landing menu —
  // see leaveToLanding() in the menu bootstrap. createEndScreen takes the
  // callback so the button isn't hard-wired to location.reload().
  endScreen = createEndScreen(() => leaveToLanding());

  const t1 = world.spawns.twoTeam.team1;
  const ty = world.sampleHeight(t1.x, t1.y);
  controls.target.set(t1.x, ty, t1.y);
  // MOBA-style 3/4 framing at the default zoom. The offset is built directly from
  // FIXED_POLAR/FIXED_YAW × DEFAULT_CAM_DIST so pitch + yaw match the locked
  // clamps and the starting distance is exactly the default (no reliance on
  // update() clamping an over-far offset). Spherical: theta (yaw) measured
  // around +Y from +Z, so yaw 0 = camera south of the hero.
  camera.position.set(
    t1.x + DEFAULT_CAM_DIST * Math.sin(FIXED_POLAR) * Math.sin(FIXED_YAW),
    ty + DEFAULT_CAM_DIST * Math.cos(FIXED_POLAR),
    t1.y + DEFAULT_CAM_DIST * Math.sin(FIXED_POLAR) * Math.cos(FIXED_YAW),
  );
  controls.update();

  // Camera/occlusion debug tuning panel (toggle with `). Lets us dial the locked
  // pitch / FOV / follow+zoom smoothing by eye; values persist to localStorage
  // and are re-applied here on the next boot. Tuning aid — bake chosen values
  // + remove.
  const cameraDebugApi: CameraDebugApi = {
    setFollowTauMs(ms) { followTauMs = ms; },
    setZoomTauMs(ms) { zoomTauMs = ms; },
    setPitchDeg(deg) {
      // Pitch is enforced via the polar clamp; the per-frame controls.update()
      // (damping) snaps the camera to it. polar measured from +Y, so
      // polar = 90° − elevation-above-horizon.
      const polar = THREE.MathUtils.degToRad(90 - deg);
      controls.minPolarAngle = polar;
      controls.maxPolarAngle = polar;
    },
    setYawDeg(deg) {
      // Yaw is enforced the same way as pitch: an azimuth clamp with min===max
      // that the per-frame controls.update() snaps the camera to. enableRotate
      // stays false — the player still can't spin the view; this just re-aims
      // the LOCKED direction. 0° = camera south of the hero looking north;
      // shipped default = FIXED_YAW (0° since the 2026-07-05 N-S map flip);
      // positive rotates the world clockwise on screen. All
      // pan paths stay correct under rotation: arrow/edge pan derives its axes
      // from camera.getWorldDirection and middle-drag raycasts through the
      // camera (Aviv yaw experiment, 2026-07-02).
      const az = THREE.MathUtils.degToRad(deg);
      controls.minAzimuthAngle = az;
      controls.maxAzimuthAngle = az;
    },
    setFovDeg(deg) { camera.fov = deg; camera.updateProjectionMatrix(); },
    setCanopyFadeEnabled(v) {
      canopyFadeEnabledDebug = v;
      // Also flip the controller itself (bug fix 2026-07-02) — previously
      // this setter only drove the shader uniform, so toggling off left any
      // in-flight fades frozen in the controller's `active` map and still
      // reporting isInstanceFaded()=true, which poisoned x-ray gating on
      // those specific trees (see the frame-loop comment near
      // updateXrayOcclusion). setEnabled() snaps to solid + clears the map.
      canopyFade?.setEnabled(v);
    },
    defaults: {
      followTauMs: DEFAULT_FOLLOW_TAU_MS,
      zoomTauMs: DEFAULT_ZOOM_TAU_MS,
      pitchDeg: 90 - THREE.MathUtils.radToDeg(FIXED_POLAR),
      yawDeg: THREE.MathUtils.radToDeg(FIXED_YAW),
      fovDeg: camera.fov,
      canopyFadeEnabled: true,
    },
  };
  applyStoredCameraDebug(cameraDebugApi);
  cameraDebug = createCameraDebug(cameraDebugApi);

  hud = createHud();

  // World hover tooltip — cursor-anchored "what is that?" panel. Fed per frame
  // from computeHover() below.
  hoverTooltip = createHoverTooltip();

  // Click-pinned unit inspect panel — fed per frame from refreshInspect() below.
  inspectPanel = createInspectPanel();

  // v10 main HUD plate — owns the bottom plate cluster (portrait, equip,
  // abilities, backpack, HP/Mana/Stamina bars-strip, warmth/hunger orbs).
  // Send-fn closure forwards to room.send so the factory doesn't need a Room
  // reference. The isAnyModalOpen predicate composes every other menu's
  // isOpen() so the hotkey-1-9 suppression list lives in one place. Z-slot
  // click forwards to the build-menu toggle so the player can open the build
  // shelf with mouse OR keyboard.
  mainPlate = createMainPlate(
    (type, payload) => { room?.send(type, payload as never); },
    () => isAnyModalOpen(),
    () => zmenu?.toggle(),
    () => recenterOnHero(), // click portrait → recenter camera (issue #5 recovery)
  );
  petFrame = createPetFrame();
  // Top-left gold counter (v10 §1: gold is the only HUD currency counter).
  goldCounter = createGoldCounter();
  // Top-right "?" Help button + how-to-play modal (replaces the old hint strip).
  helpPanel = createHelpPanel();
  // Cooking prep progress bar — driven by craft-prep / craft-done broadcasts.
  cookProgress = createCookProgress(flashToast);
  // Always-open expertise arc — left-edge vertical stack of the 5 trees with
  // 0–25 bars + 5 perk pips, click-to-spend, hover summaries, banked-point
  // badge. Send-fn closure forwards to room.send (no Room ref needed); the
  // spendExpertisePoint route is server-authoritative + gated.
  expertiseArc = createExpertiseArc((type, payload) => { room?.send(type, payload as never); });
  // Off-screen hero edge-arrow (issue #5 recovery) — click to recenter.
  offscreenHero = createOffscreenHero(() => recenterOnHero());
  // New-player onboarding (issue #1): just-in-time tips + objective ticker.
  onboarding = createOnboarding();
  // Directional edge arrows (feedback #7): amber → nearest fire while freezing,
  // light-green → off-screen teammates.
  directionArrows = createDirectionArrows();
  const floatersLayer = document.getElementById("floaters-layer")!;
  floaterManager = createFloaterManager(scene, floatersLayer);
  // F2 toggles HUD layout-editor (drag groups onto painted bezels, copy coords).
  installLayoutEditor();
  // Q/W/E/R abilities render directly in the main-plate painted circles
  // (hud/main-plate.ts `refresh` reads cube.spellbook.slot* + cooldowns) — the
  // former standalone floating ability-bar was removed so the spells sit inside
  // the HUD bezels instead of a misaligned overlay.
  // Pass flashToast as the notify callback so the craft menu can surface
  // "Can't craft X: need 3 more lumber" / "Crafting: Y" feedback. The
  // server's craft route is silent on failure by design (it can't push
  // per-player error toasts cheaply), so the menu does its own pre-flight.
  craftMenu = createCraftMenu(
    (type, payload) => { room?.send(type, payload as never); },
    flashToast,
  );
  // Tavern spell shop — same modal pattern as the craft menu. Opens at a
  // friendly Tavern (E-key or the building menu's "Spell Shop" action); the
  // server's learnAbility/bindSlot routes own all validation, this drives UI.
  tavernShop = createTavernShop(
    (type, payload) => { room?.send(type, payload as never); },
    flashToast,
  );
  // Hunter's Lodge mastery panel is now hosted inside the craft menu as custom tabs.
  // (createPetMasteryPanel retired; buildPetMasteryRows/buildTrophyRows used by craft-menu.ts)
  corpseLoot = createCorpseLoot((type, payload) => {
    room?.send(type, payload as never);
  });
  corpseRender = createCorpseRender(scene, world!.sampleHeight, world!.assets);

  // The Colyseus connection is owned by the menu bootstrap (a single `client`
  // created once; landing/lobby join or create the room and hand it here). The
  // reconnect/joinOrCreate logic that used to live here is gone — `room` is the
  // `joinedRoom` parameter (assigned at the top of this function), so the same
  // joined room object (with its server-assigned StateView) is reused into the
  // game without re-joining.
  const RECONNECT_KEY = "tribez:reconnectToken";

  // Connection-lost handling. colyseus.js fires onLeave with code 1000 for a
  // clean/consented close and an abnormal code (1006, …) for a network drop.
  // On a drop we persist the token, show the overlay, and reload into a reconnect
  // attempt while the server's grace window is still open.
  const connOverlay = document.getElementById("conn-overlay")!;
  const connMsg = document.getElementById("conn-msg")!;
  const connReload = document.getElementById("conn-reload")!;
  connReload.addEventListener("click", () => location.reload());
  let reconnecting = false;
  const handleDrop = (reason: string) => {
    if (reconnecting) return;
    reconnecting = true;
    console.warn(`[net] connection lost (${reason}) — attempting reconnect`);
    if (room?.reconnectionToken) {
      sessionStorage.setItem(RECONNECT_KEY, room.reconnectionToken);
    }
    connMsg.textContent = "Reconnecting…";
    connReload.classList.add("hidden");
    connOverlay.classList.remove("hidden");
    // Brief beat so the overlay paints before the reload tears the page down.
    setTimeout(() => location.reload(), 1200);
  };
  room.onLeave((code) => {
    if (code === 1000) return; // clean close (we asked to leave)
    handleDrop(`code ${code}`);
  });
  room.onError((code, message) => {
    console.error(`[net] room error ${code}: ${message ?? ""}`);
    handleDrop(`error ${code}`);
  });

  // Latency probe — round-trip a tiny ping every 2s; the server echoes "pong"
  // with the same id and we record the RTT for the FPS overlay. Bounded map so
  // a stretch of dropped pongs can't leak. The interval dies with the page on
  // reconnect (handleDrop reloads), so no explicit teardown is needed.
  const pingSent = new Map<number, number>();
  let pingSeq = 0;
  room.onMessage("pong", (msg: { id: number }) => {
    const sent = pingSent.get(msg.id);
    if (sent === undefined) return;
    pingSent.delete(msg.id);
    latestPingMs = performance.now() - sent;
  });
  window.setInterval(() => {
    if (!room) return;
    const id = pingSeq++;
    pingSent.set(id, performance.now());
    if (pingSent.size > 8) pingSent.delete(pingSent.keys().next().value!);
    room.send("ping", { id });
  }, 2000);

  // Slice D placeholder world-VFX — subscribes to cast/toggle/channel
  // broadcasts so every ability becomes visible in the 3D world. Must run
  // AFTER joinOrCreate resolves (subscribes to room.onMessage). Caster
  // lookup resolves sessionId → players.get(id)?.object so persistent
  // shapes follow the moving caster and unit-tag shapes anchor to the live
  // mesh at spawn.
  castVfx = installCastVfx({
    scene,
    room,
    getCasterObject: (id) => players.get(id)?.object,
    // Ground-decal shapes (point-burst rings, zone discs) sit on the terrain.
    // `world` is loaded by this point (await loadWorld resolved much earlier).
    sampleHeight: (x, z) => world!.sampleHeight(x, z),
  });

  // Audio runtime — the trigger bridge (broadcasts → SFX) mirrors castVfx, and
  // the director owns the ambience/music beds + day/night crossfades + survival
  // cues. Both are no-ops if the manifest failed to load (audio === null).
  if (audio) {
    installAudioTriggers({
      room,
      audio,
      getPlayerPos: (id) => players.get(id)?.object.position,
    });
    audioDirector = installAudioDirector({ room, audio });
  }

  // Combat broadcasts — separate from schema delta sync.
  const tmpHitVec = new THREE.Vector3();
  room.onMessage("hit", (msg: { sourceId: string; targetId: string; amount: number; attackKind?: string; isCrit: boolean; hpAfter: number }) => {
    const player = players.get(msg.targetId);
    if (!player || !hud) return;
    // Spawn damage text 1.5u above the target's head (player root pos + scale)
    tmpHitVec.copy(player.object.position).add(new THREE.Vector3(0, 1.6, 0));
    hud.showDamage(tmpHitVec, msg.amount, msg.isCrit, msg.attackKind ?? "basicAttack", camera);
  });

  room.onMessage("swing", (msg: { sourceId: string; targetId: string }) => {
    attackTargetById.set(msg.sourceId, msg.targetId);
    const player = players.get(msg.sourceId);
    if (!player) return;
    player.setAction("attacking");
    attackingUntil.set(msg.sourceId, Date.now() + 500);
  });

  room.onMessage("projectile-hit", (m: { id: string; x: number; z: number }) => {
    if (!world) return;
    const poof = createPointBurst({ color: 0xffcc44, radius: 0.5, durationMs: 400 });
    poof.object.position.set(m.x, world.sampleHeight(m.x, m.z) + 0.05, m.z);
    scene.add(poof.object);
    projectilePoofs.push(poof);
  });

  room.onMessage("death", (msg: { killerId: string; victimId: string }) => {
    console.log(`[combat] ${msg.killerId.slice(0,4)} killed ${msg.victimId.slice(0,4)}`);
  });

  room.onMessage("respawn", (msg: { playerId: string }) => {
    console.log(`[combat] ${msg.playerId.slice(0,4)} respawned`);
  });

  room.onMessage("blocked", (msg: { sourceId: string; targetId: string }) => {
    // Reserved for future: visual indicator that an attack was countered.
    void msg;
  });

  // creature-death broadcasts arrive after the schema removes the entity, so we
  // can't animate the (already-despawned) mesh — instead poof a short dust burst
  // where it fell, so a death reads as an event rather than a silent pop. The
  // server now carries the death position (x/z) for exactly this. Pets in
  // particular used to stand frozen on death; the server reap removes them and
  // this gives the disappearance a beat of feedback (Aviv 2026-06-30).
  room.onMessage("creature-death", (msg: { creatureId: string; killerId: string; x?: number; z?: number }) => {
    if (!world || msg.x === undefined || msg.z === undefined) return;
    const poof = createPointBurst({ color: 0x9a7b55, radius: 0.7, durationMs: 500 });
    poof.object.position.set(msg.x, world.sampleHeight(msg.x, msg.z) + 0.1, msg.z);
    scene.add(poof.object);
    projectilePoofs.push(poof);
  });

  // Pet beam — Nightwisp fires a visual beam from its orb to the attack target.
  // sourceId = pet creature id, targetId = target player/creature id (optional),
  // x/z = fallback world position if targetId isn't visible on this client.
  room.onMessage("pet-beam", (msg: { sourceId: string; targetId: string; x: number; z: number }) => {
    const src = creatureRender?.getObject(msg.sourceId);
    if (!src) return;
    const from = src.position.clone();
    from.y = Math.max(from.y + 3.2, 2.5); // offset to match the orb's y=3.2 position
    const tgtObj = players.get(msg.targetId)?.object ?? creatureRender?.getObject(msg.targetId);
    const to = tgtObj
      ? tgtObj.position.clone()
      : new THREE.Vector3(msg.x, world?.sampleHeight(msg.x, msg.z) ?? 0, msg.z);
    const beam = createBeam({ color: 0x9b6cff, from, to, durationMs: 300 });
    scene.add(beam.object);
    petBeams.push(beam);
  });

  // Building action toasts — only show toasts for actions the local player initiated.
  // Server broadcasts to everyone for QA log scraping; we filter on `by`. The
  // "(placeholder — ships with items)" suffix was for the pre-Phase-4 era when
  // craft buttons were dead clicks; now Forge/Workbench route through the
  // craft menu (no building-action), so the remaining building-action paths
  // (Farm foxBait/domesticatedSheep, SpikeTrap sharpen, Tavern, Lodge, etc.)
  // are the actually-placeholder ones.
  room.onMessage("building-action-fired", (msg: { id: string; action: string; by: string }) => {
    if (msg.by !== mySessionId) return;
    flashToast(tradeActionToast(msg.action));
  });

  // StorageOutpost trade rejection → toast. The server validates (hold / gold /
  // lock / full pack) and only pushes this on failure.
  room.onMessage("trade-rejected", (msg: { action: string; reason: string }) => {
    const t = parseTradeActionLabel(msg.action);
    const why = msg.reason === "none" ? `No ${t} to sell`
      : msg.reason === "gold" ? "Not enough gold"
      : msg.reason === "locked" ? "Outpost is locked"
      : msg.reason === "full" ? "Backpack full"
      : "Trade failed";
    flashToast(why);
  });

  // Tavern spell-buy rejection → toast. The server's purchaseAbility validates
  // (range / gold / rider / already-learned) and only pushes this on failure;
  // success is reflected silently via the learned[]/slot schema sync. The
  // shop pre-flights gold + rider, so the common toast here is "Too far from a
  // Tavern" (range can't be fully mirrored client-side without the building).
  room.onMessage("learnFailed", (msg: { abilityId: string; reason: string }) => {
    const name = ABILITIES[msg.abilityId]?.name ?? msg.abilityId;
    const why: Record<string, string> = {
      dead: "Can't learn spells while dead",
      "unknown-ability": "Unknown spell",
      "not-buyable": "That spell isn't for sale",
      "already-learned": `Already learned ${name}`,
      "out-of-range": "Too far from a friendly Tavern",
      "insufficient-gold": `Not enough gold for ${name}`,
      "missing-resource": `Missing a resource for ${name}`,
    };
    flashToast(why[msg.reason] ?? `Couldn't learn ${name}`);
  });

  // Unreachable move/harvest click (server found no reachable ground near it) →
  // red double-blink ring at the click. The denied sound rides the audio trigger
  // bus (audio/triggers.ts).
  room.onMessage("move-rejected", (msg: { x: number; z: number }) => {
    showRejected(msg.x, msg.z);
  });

  // Tavern consumable-buy rejection → toast. Like learnFailed, only pushed on
  // failure; success reflects silently via gold + backpack schema sync.
  room.onMessage("buyItemFailed", (msg: { itemId: string; reason: string }) => {
    const name = ITEMS[msg.itemId]?.displayName ?? msg.itemId;
    const why: Record<string, string> = {
      dead: "Can't shop while dead",
      "unknown-item": "That item isn't for sale",
      "not-buyable": "That item isn't sold here",
      "out-of-range": "Too far from a friendly Tavern",
      "insufficient-gold": `Not enough gold for ${name}`,
      "backpack-full": "Backpack is full",
    };
    flashToast(why[msg.reason] ?? `Couldn't buy ${name}`);
  });

  // Pet-purchase rejection → toast. Success reflects silently via pet-spawn schema sync.
  room.onMessage("purchase-nightwisp-rejected", (msg: { reason: string }) => {
    const why: Record<string, string> = {
      dead: "Can't shop while dead",
      "out-of-range": "Too far from a friendly Tavern",
      "insufficient-gold": "Not enough gold for Nightwisp (need 45g)",
      "pet-cap": "You've reached the pet cap",
    };
    flashToast(why[msg.reason] ?? `Couldn't buy Nightwisp: ${msg.reason}`);
  });

  // Pet mastery upgrade → toast. Success: bit set in taughtPets bitmask (schema
  // sync reflects it in the tile automatically via refresh()). The panel also
  // pre-flights affordability, so the most common server rejection here is range.
  room.onMessage("pet-upgrade-ok", (msg: { upgradeId: string }) => {
    // Import findUpgrade from shared via the panel module indirection isn't needed
    // here — just show the id as a readable name from PET_UPGRADES lookup.
    const label = msg.upgradeId.replace(/([A-Z])/g, " $1").trim();
    flashToast(`Learned: ${label}`);
  });
  room.onMessage("pet-upgrade-rejected", (msg: { reason: string }) => {
    const why: Record<string, string> = {
      dead: "Can't learn while dead",
      "unknown-upgrade": "Unknown upgrade",
      "out-of-range": "Too far from an allied Hunter's Lodge",
      "already-purchased": "Already learned that upgrade",
      "insufficient-resources": "Can't afford that upgrade",
    };
    flashToast(why[msg.reason] ?? `Upgrade failed: ${msg.reason}`);
  });

  // Heal-bear cooldown feedback (G key). Success is silent — HP rising on the
  // portrait is sufficient feedback; a toast would fire every key press.
  room.onMessage("heal-bear-cooldown", (msg: { remainingMs: number }) => {
    const secs = Math.ceil(Math.max(0, msg.remainingMs) / 1000);
    flashToast(`Bear heal on cooldown (${secs}s)`);
  });

  // Machete cripple-throw cooldown feedback (C key / main-hand active).
  room.onMessage("use-machete-cooldown", (msg: { remainingMs: number }) => {
    const secs = Math.ceil(Math.max(0, msg.remainingMs) / 1000);
    flashToast(`Cripple on cooldown (${secs}s)`);
  });

  // Bait throw cooldown feedback (X key / off-hand active).
  room.onMessage("use-bait-cooldown", (msg: { remainingMs: number }) => {
    const secs = Math.ceil(Math.max(0, msg.remainingMs) / 1000);
    flashToast(`Bait on cooldown (${secs}s)`);
  });

  // Trophy deposit → toast. Success: server consumes the backpack item (schema
  // sync reflects it); lodge deposited-state is not client-synced (server rejects
  // duplicates via "already-deposited" toast).
  room.onMessage("collect-trophy-ok", (msg: { trophyType: string }) => {
    const label = msg.trophyType.replace(/([A-Z])/g, " $1").trim();
    flashToast(`Deposited: ${label}`);
  });
  room.onMessage("collect-trophy-rejected", (msg: { reason: string }) => {
    const why: Record<string, string> = {
      dead: "Can't deposit while dead",
      "unknown-trophy": "Unknown trophy type",
      "out-of-range": "Too far from an allied Hunter's Lodge",
      "item-not-in-backpack": "That trophy isn't in your backpack",
      "already-deposited": "That trophy is already deposited at this lodge",
    };
    flashToast(why[msg.reason] ?? `Deposit failed: ${msg.reason}`);
  });

  // Cooking prep timer (#5): the server defers Campfire crafts and broadcasts a
  // start (craft-prep) + completion (craft-done). Only the local player's cook
  // drives the progress bar; other players' cooks are ignored.
  room.onMessage("craft-prep", (msg: { sessionId: string; recipeId: string; totalMs: number }) => {
    if (msg.sessionId !== mySessionId) return;
    cookProgress?.start(msg.recipeId, msg.totalMs);
  });
  room.onMessage("craft-done", (msg: { sessionId: string; recipeId: string; success: boolean }) => {
    if (msg.sessionId !== mySessionId) return;
    cookProgress?.finish(msg.recipeId, msg.success);
  });

  // Craft rejection → toast. handleCraft used to silently no-op on every gate
  // (Artisanship tier, materials, station proximity, …), so a blocked craft just
  // "did nothing". Now it pushes a reason and we tell the player WHY. The tier =
  // ceil(req / 5 KTS-levels-per-tier).
  room.onMessage(
    "craft-rejected",
    (msg: { recipeId: string; reason: string; itemId?: string; req?: number; building?: string }) => {
      const name = msg.itemId ? (ITEMS[msg.itemId]?.displayName ?? msg.itemId) : "that item";
      const tier = msg.req ? Math.ceil(msg.req / 5) : 0;
      const building =
        msg.building === "HuntersLodge" ? "Hunter's Lodge" : (msg.building ?? "crafting station");
      const why: Record<string, string> = {
        dead: "Can't craft while dead",
        "unknown-recipe": "Unknown recipe",
        artisanship: `Requires Artisanship tier ${tier}`,
        cooking: `Requires Cooking tier ${tier}`,
        "insufficient-cost": `Not enough materials for ${name}`,
        "no-station": `Build a ${building} (press Z) to craft this.`,
        "in-progress": "A craft is already in progress",
        "backpack-full": "Backpack is full",
      };
      flashToast(why[msg.reason] ?? `Couldn't craft ${name}`);
    },
  );

  // Stub listeners for server broadcasts the client doesn't act on yet. Without
  // these, colyseus.js logs "onMessage for type X not registered" warnings every
  // time the server fires a building/wonder/match event. Keep them registered so
  // the console stays clean and so future UI hooks (toast on team-defeated,
  // muzzle-flash on turret-fire, etc.) have a clear attach point.
  room.onMessage("building-placed", () => {});
  room.onMessage("building-ready", () => {});
  room.onMessage("building-killed", () => {});
  room.onMessage("building-salvaged", () => {});
  room.onMessage("build-rejected", () => {});
  room.onMessage("salvage-rejected", () => {});
  room.onMessage("trap-fire", () => {});
  room.onMessage("turret-fire", () => {});
  room.onMessage("wonder-timer-start", () => {});
  room.onMessage("wonder-timer-extend", () => {});
  room.onMessage("wonder-timer-expire", () => {});
  room.onMessage("team-defeated", () => {});
  room.onMessage("match-end", () => {});
  room.onMessage("creature-swing", () => {});

  const $ = getStateCallbacks(room);

  $(room.state).cubes.onAdd((cube, sessionId) => {
    const isOwn = sessionId === mySessionId;
    const me = mySessionId ? room!.state.cubes.get(mySessionId) : null;
    // Without our own cube yet (we're the first to join), can't compute team-relative tint.
    // Default to enemy tint and update once `me` exists; the per-frame tint update in Task 21
    // covers the recompute.
    const initialTint =
      isOwn ? TINT_OWN
      : (cube.teamId === 99) ? TINT_DUMMY
      : (me && cube.teamId === me.teamId) ? TINT_ALLY
      : TINT_ENEMY;
    const player = createPlayer(world!.assets.vikingMale, initialTint);
    // Guard against partially-decoded schema state on first onAdd: a Colyseus
    // delta sync that's missing field updates leaves cube.x / cube.z as NaN
    // (or undefined → NaN under arithmetic). Once a NaN lands in
    // player.object.position, the per-frame lerp `pos.x += (cube.x - NaN) * 0.25`
    // perpetuates NaN forever — the mesh disappears and never recovers.
    // Fall back to (0, 0) here; per-frame lerp converges to cube.x/z once a
    // valid delta arrives.
    const spawnX = Number.isFinite(cube.x) ? cube.x : 0;
    const spawnZ = Number.isFinite(cube.z) ? cube.z : 0;
    const y = world!.sampleHeight(spawnX, spawnZ);
    player.object.position.set(spawnX, y, spawnZ);
    scene.add(player.object);
    players.set(sessionId, player);
    // FIX 3b — record authoritative positions into the interp buffer at PATCH
    // ARRIVAL, not when the render loop first observes them. Sampling cube.x/z in
    // rAF timestamps every snapshot with the frame clock, so unevenly-spaced
    // patches get quantized to frame boundaries → uneven Δt → velocity ripple.
    // A per-cube onChange fires once per decoded state patch; timestamping with
    // performance.now() THERE captures the true arrival time and spacing. record()
    // dedupes on unchanged x/z, so onChange firing for non-positional fields (hp,
    // buffs, …) or an idle unit adds nothing — the buffer still freezes on arrival.
    {
      let buf = interpBuffers.get(sessionId);
      if (!buf) { buf = new SnapshotBuffer(); interpBuffers.set(sessionId, buf); }
      buf.record(spawnX, spawnZ, performance.now()); // seed so sample() has data pre-patch
      $(cube).onChange(() => {
        if (!Number.isFinite(cube.x) || !Number.isFinite(cube.z)) return;
        buf!.record(cube.x, cube.z, performance.now());
      });
    }
    // sprite-bar + rich-overlay handled by floaterManager.tick() per frame
  });

  $(room.state).cubes.onRemove((_cube, sessionId) => {
    attackingUntil.delete(sessionId);
    const player = players.get(sessionId);
    if (!player) return;
    scene.remove(player.object);
    player.dispose();
    players.delete(sessionId);
    interpBuffers.delete(sessionId); // FIX A: drop the snapshot-interp history
    clearDashTrailVfx(sessionId); // Task 9: drop the dash position-delta cache
    // sprite-bar + rich-overlay culled by floaterManager.tick() next frame
  });

  // Creatures record into their snapshot-interp buffers at PATCH DECODE, the
  // same FIX 3b contract as cubes above — the render loop's reconcile() would
  // re-quantize snapshot timestamps to frame boundaries. record() dedupes on
  // unchanged x/z, so onChange firing for non-positional fields adds nothing.
  $(room.state).creatures.onAdd((creature, id) => {
    if (Number.isFinite(creature.x) && Number.isFinite(creature.z)) {
      creatureRender?.record(id, creature.x, creature.z, performance.now());
    }
    $(creature).onChange(() => {
      if (!Number.isFinite(creature.x) || !Number.isFinite(creature.z)) return;
      creatureRender?.record(id, creature.x, creature.z, performance.now());
    });
  });

  $(room.state).resources.onAdd((node, id) => {
    resources?.add(id, node);
  });
  $(room.state).resources.onRemove((_node, id) => {
    resources?.remove(id);
  });

  // Kick the render loop now that the scene, renderers, HUD, and live-room
  // subscriptions are all wired. `frame` is a hoisted module-scope function
  // declaration (defined below); by the time initGameClient is invoked from the
  // menu bootstrap the module has fully evaluated, so the reference is live.
  // The loading screen is dismissed on the FIRST painted frame (see `frame`),
  // so the live scene is actually on-screen before the overlay lifts.
  loadingScreen.milestone(1);
  dismissLoadingOnFrame = true;
  requestAnimationFrame(frame);
}

// ── Menu bootstrap + screen controller ───────────────────────────────────────
// Boots to the landing menu — NO auto-join, NO three.js build. One Colyseus
// `client` is created here; landing/lobby create or join a room and route it by
// phase (lobby → lobby screen; running → initGameClient builds the 3D game).
// Play Again = consented leave + reload back to this landing screen.
//
// StateView note: the server assigns each client's StateView at lobby onJoin, so
// the SAME joined room object is threaded through routeByPhase → enterLobby →
// enterGame; we never re-join/reconnect to enter the game.
const wsUrl = (import.meta.env.VITE_SERVER_URL as string | undefined)
  ?? `ws://${location.hostname}:2567`;
const client = new Client(wsUrl);

let activeRoom: Room<HelloState> | null = null;
let roomWatched: Room<HelloState> | null = null; // guard one phase-watcher per room
let pollTimer: number | null = null;

const landing = createLanding({
  onQuickMatch: async (name) => {
    audio?.unlock(); // first user gesture — satisfies browser autoplay gate
    try {
      const r = await client.create<HelloState>("hello", { quickStart: true, name });
      stopPoll(); landing.hide(); routeByPhase(r);
    } catch (e) { flashToast(`Quick Match failed: ${(e as Error).message}`); }
  },
  onCreate: async (name) => {
    audio?.unlock();
    try {
      const r = await client.create<HelloState>("hello", { name });
      stopPoll(); landing.hide(); enterLobby(r);
    } catch (e) { flashToast(`Create failed: ${(e as Error).message}`); }
  },
  onJoin: async (roomId, name, password) => {
    audio?.unlock();
    try {
      const r = await client.joinById<HelloState>(roomId, { name, password: password ?? undefined });
      stopPoll(); landing.hide(); routeByPhase(r);
    } catch (e) { flashToast(`Join failed: ${(e as Error).message}`); pollRooms(); }
  },
  onRefresh: () => pollRooms(),
});

const lobby = createLobbyScreen({
  send: (type, payload) => activeRoom?.send(type, payload as never),
  onLeave: () => leaveToLanding(),
});

// Boot/asset-load gate (menu → live scene). Shown by enterGame, dismissed on the
// first painted frame (see `dismissLoadingOnFrame` in `frame`).
const loadingScreen = createLoadingScreen();
let dismissLoadingOnFrame = false;

function routeByPhase(r: Room<HelloState>): void {
  activeRoom = r;
  if (r.state.matchState === "running") { enterGame(r); return; }
  enterLobby(r);
}

function enterLobby(r: Room<HelloState>): void {
  activeRoom = r;
  // Menu/lobby bed — idempotent (loop() no-ops if already running); the audio
  // director stops it when the match starts.
  audio?.loop("music.menu", { fadeMs: 1200 });
  lobby.show();
  lobby.refresh(r.state, r.sessionId);
  if (roomWatched !== r) {
    roomWatched = r;
    r.onStateChange(() => {
      // Once the game is built, this watcher has nothing left to do — return
      // immediately rather than churn lobby.refresh/enterGame every 30Hz patch.
      if (gameInited) return;
      if (r.state.matchState === "running") enterGame(r); // enterGame hides the lobby
      else if (r.state.matchState === "lobby") lobby.refresh(r.state, r.sessionId);
    });
  }
}

function enterGame(r: Room<HelloState>): void {
  lobby.hide(); landing.hide();
  loadingScreen.show(); // gate the blank-frame window while initGameClient loads
  void initGameClient(r);
}

function leaveToLanding(): void {
  try { activeRoom?.leave(true); } catch { /* already gone */ }
  sessionStorage.removeItem("tribez:reconnectToken");
  location.reload(); // boots fresh to landing (functional; SPA-smooth teardown deferred)
}

// colyseus.js 0.16 dropped `client.getAvailableRooms` and this build serves
// matchmaking over the WS transport (no HTTP listing route), so the navigator
// polls the server's custom `/rooms` endpoint (index.ts → matchMaker.query).
// `client.http.get` reuses the client's endpoint base (the http(s) form of the
// ws URL). On failure we log + leave the list empty (graceful "no open lobbies").
type LobbyMeta = { name?: string; status?: string; humans?: number; capacity?: number; private?: boolean; joinable?: boolean };
type RoomListItem = { roomId: string; clients: number; maxClients: number; metadata?: LobbyMeta };
async function pollRooms(): Promise<void> {
  try {
    const res = await client.http.get<RoomListItem[]>("rooms");
    const rooms = Array.isArray(res.data) ? res.data : [];
    const rows: RoomRow[] = rooms.map((r) => ({
      roomId: r.roomId,
      lobbyName: r.metadata?.name ?? "Lobby",
      status: r.metadata?.status ?? "lobby",
      humans: r.metadata?.humans ?? r.clients,
      capacity: r.metadata?.capacity ?? r.maxClients,
      isPrivate: r.metadata?.private ?? false,
      joinable: r.metadata?.joinable ?? true,
    }));
    landing.setRooms(rows);
  } catch (e) { console.warn("[menu] poll failed", e); }
}
function startPoll(): void { pollRooms(); pollTimer = window.setInterval(pollRooms, 3000); }
function stopPoll(): void { if (pollTimer !== null) { clearInterval(pollTimer); pollTimer = null; } }

function showLanding(): void {
  landing.show();
  startPoll();
  // Resume menu music when browsing lobbies (initial visit relies on the first
  // gesture; returning from a match needs an explicit nudge since the director
  // stopped it). loop() is idempotent — a no-op if it's already playing.
  if (audioGestured) startMenuAudio();
}

// Mid-match reconnect: on an abnormal drop, handleDrop() stashes the room's
// reconnectionToken and reloads. On boot, reclaim the in-progress match (the
// server's 30s allowReconnection window) and route straight back into it,
// skipping the menu. Token is one-shot (removed before the attempt) so a stale
// token never loops. Falls back to the landing screen if reconnect fails.
const savedToken = sessionStorage.getItem("tribez:reconnectToken");
if (savedToken) {
  sessionStorage.removeItem("tribez:reconnectToken");
  void (async () => {
    try {
      const r = await client.reconnect<HelloState>(savedToken);
      routeByPhase(r);
    } catch {
      showLanding();
    }
  })();
} else {
  showLanding();
}

// FIX A — buffered snapshot interpolation render delay. The rendered clock sits
// this far behind "now" so there is (almost) always a future authoritative
// snapshot to interpolate toward. The server PATCHES at 30 Hz (~33 ms) — the
// room broadcasts a patch at the end of every sim tick (phase-locked, 2026-07-10;
// the stock Colyseus default is a free-running 50 ms / 20 Hz interval, which
// under-fed this buffer and added a random phase offset). At 30 Hz
// patches, 66 ms is ~2 patches: matches Dota/LoL (interp_ratio 2 at 30 Hz) and
// trims ~34 ms of dead latency vs the old 100 ms. One extra patch of jitter
// cushion remains. NOTE our transport is TCP WebSocket (not Dota's UDP), so
// occasional bunched packets can briefly out-run a 2-patch buffer → a rare
// hold/hitch; raise back toward 100 ms if that jitter reads worse than the
// latency win. LIVE TUNABLE via window.__setInterpDelay(ms) for feel-testing
// 50/66/100 on the real network without a rebuild.
let INTERP_DELAY_MS = 66;
// Feel-test knob: set the interp render delay live from the console, e.g.
// __setInterpDelay(50). Applies to the local hero AND all remotes on the next
// frame. Dev/tuning aid — the shipped default is the const initializer above.
(window as unknown as { __setInterpDelay: (ms: number) => string }).__setInterpDelay = (ms: number) => {
  const clamped = Math.max(0, Math.min(300, Math.round(ms)));
  INTERP_DELAY_MS = clamped;
  return `INTERP_DELAY_MS = ${clamped}ms (~${(clamped / (1000 / 30)).toFixed(1)} patches @30Hz)`;
};
// FIX B — run-vs-idle threshold on ACTUAL rendered speed (units/sec). Base run
// speed is 2.5 u/s (cube.moveSpeed 250), so 0.1 u/s is far below any real gait
// yet above interpolation jitter. On arrival the buffer freezes and rendered
// displacement is exactly 0, so this only has to reject sub-pixel noise — no
// time-based hysteresis, so step-off stays responsive.
const RENDER_MOVE_EPS = 0.1;
// Per-entity authoritative-position history for the interpolator (FIX A). Keyed
// by sessionId; entries are dropped in cubes.onRemove.
const interpBuffers = new Map<string, SnapshotBuffer>();
// Baseline pan speed. Halved from the original 45 (2026-06-18) so the default
// feel is calmer; players can scale it back up (to 2.0× = the old 45) or down
// via the Camera Speed slider in Settings. The live multiplier is read per
// frame in panCamera() from camera-settings.ts (localStorage-persisted).
const BASE_PAN_SPEED = 22.5;
// Manual-scroll clamp (issue #3): keep the look-at target within the map so you
// can't deliberately scroll the camera off into the void past the world edge.
// A small margin lets you frame entities sitting right on the boundary; the
// horizon dome + apron (issue #4) cover whatever sliver of beyond-terrain the
// camera's pull-back still reveals. (The follow loop is intentionally NOT
// clamped — following a hero standing at the edge must keep them in view.)
const PAN_MARGIN = 8;
const PAN_MIN = -PAN_MARGIN;
const PAN_MAX = MAP_UNITS + PAN_MARGIN;
// Mouse-edge panning: cursor within EDGE_PAN_PX of a viewport edge pans the
// camera that direction at full speed. RTS/MOBA convention; replaces WASD.
const EDGE_PAN_PX = 48;

// HUD-guard for edge-pan (2026-07-02, ships edge-pan ON by default). Reaching
// for a HUD button/minimap/panel that happens to sit near a screen edge used
// to trigger an inadvertent pan — that's exactly why edge-pan shipped OFF
// (playtest 2026-06-27). A live hit-test against whatever element is actually
// on top at that pixel is robust to HUD layout changes, unlike a hardcoded
// rect allowlist. Only called when the cursor is AT an edge (see call site) —
// that's rare relative to total mousemove volume, so the one
// elementFromPoint/frame this costs is a non-issue; not worth caching/
// throttling further.
function isPointerOverCanvas(clientX: number, clientY: number): boolean {
  return document.elementFromPoint(clientX, clientY) === canvas;
}

const keys = new Set<string>();
addEventListener("keydown", (e) => keys.add(e.key.toLowerCase()));
addEventListener("keyup", (e) => keys.delete(e.key.toLowerCase()));

// Track cursor position for edge-pan. -1 = mouse hasn't entered the viewport
// yet (or just left it) — treat as "no edge-pan input".
let mouseClientX = -1, mouseClientY = -1;
addEventListener("mousemove", (e) => { mouseClientX = e.clientX; mouseClientY = e.clientY; });
// Track button-held state so the hover tooltip stays hidden during a click /
// camera-drag (spec §"Hidden during: camera-pan drag with mouse held").
addEventListener("mousedown", () => { mouseDown = true; });
addEventListener("mouseup", () => { mouseDown = false; });
// Reset edge-pan input when the pointer truly leaves the document. `window`'s
// `mouseleave` is unreliable (often never fires when the cursor exits to browser
// chrome or the OS taskbar), which left the camera edge-panning forever. Listen
// on `document` with a null `relatedTarget` (= left the page) instead.
document.addEventListener("mouseout", (e) => {
  if (e.relatedTarget === null) { mouseClientX = -1; mouseClientY = -1; }
});

// Window blur (Alt-Tab, click-away, OS focus steal): clear all held keys and
// edge-pan input. Without this a key held during focus loss never receives its
// keyup, so the camera pans (or an arrow stays "down") indefinitely on return.
addEventListener("blur", () => {
  keys.clear();
  mouseClientX = -1; mouseClientY = -1;
  midPanning = false;
});

// ── Middle-mouse drag to pan (grab-the-world) ───────────────────────────────
// The terrain point under the cursor at drag-start stays glued to the cursor as
// you drag — drag right and the world slides right (the view moves left), the
// Google-Maps / RTS idiom. Implemented by raycasting the cursor onto the terrain
// each move and translating the camera by the delta between the anchored grab
// point and where the cursor now lands; exact at any pitch/zoom (a guessed
// pixels→world scale read wrong at the 40° pitch — playtest 2026-06-26). Like the
// arrow-key scout it PARKS the camera (persistent unlock) until you Space/
// portrait-click back to the hero.
let midPanning = false;
let midGrabValid = false;
const _midGrab = new THREE.Vector3();   // world point anchored under the cursor at drag-start
const _midNdc = new THREE.Vector2();    // own NDC vec so we don't clobber the shared `ndc`
const _midPlane = new THREE.Plane();
const _midHit = new THREE.Vector3();

// Project a screen point onto the terrain (falling back to a horizontal plane at
// `planeY` when the cursor is off the mesh — sky / skirt). Writes the world hit
// into `out`; returns out, or null if even the plane misses.
function midPanProject(clientX: number, clientY: number, planeY: number, out: THREE.Vector3): THREE.Vector3 | null {
  if (!world) return null;
  const rect = canvas.getBoundingClientRect();
  _midNdc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
  _midNdc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
  raycaster.setFromCamera(_midNdc, camera);
  const hits = raycaster.intersectObject(world.terrain, false);
  if (hits.length > 0) return out.copy(hits[0]!.point);
  _midPlane.set(_up, -planeY); // horizontal plane at y = planeY
  return raycaster.ray.intersectPlane(_midPlane, out);
}

addEventListener("mousedown", (e) => {
  if (e.button !== 1) return; // middle button only
  e.preventDefault(); // suppress the browser's middle-click autoscroll
  if (!midPanProject(e.clientX, e.clientY, controls.target.y, _midGrab)) return;
  midPanning = true;
  midGrabValid = true;
  if (cameraLocked) flashToast("Free-look — press Space to snap back to your hero");
  cameraLocked = false; // park (deliberate scout)
});
addEventListener("mouseup", (e) => { if (e.button === 1) { midPanning = false; midGrabValid = false; } });
// Some browsers fire a middle-click "auxclick" that can re-trigger autoscroll;
// swallow it while we own the button.
addEventListener("auxclick", (e) => { if (e.button === 1) e.preventDefault(); });

addEventListener("mousemove", (e) => {
  if (!midPanning || !midGrabValid) return;
  // Where does the cursor hit the ground now? Pan so the anchored grab point
  // returns under it. Keep the fallback plane at the grab's height.
  if (!midPanProject(e.clientX, e.clientY, _midGrab.y, _midHit)) return;
  const dx = _midGrab.x - _midHit.x;
  const dz = _midGrab.z - _midHit.z;
  if (dx === 0 && dz === 0) return;

  const nextX = THREE.MathUtils.clamp(controls.target.x + dx, PAN_MIN, PAN_MAX);
  const nextZ = THREE.MathUtils.clamp(controls.target.z + dz, PAN_MIN, PAN_MAX);
  const appliedX = nextX - controls.target.x;
  const appliedZ = nextZ - controls.target.z;
  controls.target.x += appliedX; controls.target.z += appliedZ;
  camera.position.x += appliedX; camera.position.z += appliedZ;
});

const _fwd = new THREE.Vector3();
const _right = new THREE.Vector3();
const _up = new THREE.Vector3(0, 1, 0);
const _pan = new THREE.Vector3();
const _heroProj = new THREE.Vector3();  // reused for off-screen-hero NDC projection
const _canopyFootV = new THREE.Vector3(); // reused for the canopy-fade occlusion scan (hero ground point)
// Reused per-frame occluder-root scratch for the x-ray silhouette scan — avoids
// allocating a new array every frame (matches the other reused-scratch
// vectors/arrays declared alongside it here).
const _xrayOccluders: THREE.Object3D[] = [];

function panCamera(dt: number) {
  edgePeeking = false;

  // Arrow keys = DELIBERATE scout → park the camera (persistent unlock).
  let adx = 0, adz = 0;
  if (keys.has("arrowup"))    adz += 1;
  if (keys.has("arrowdown"))  adz -= 1;
  if (keys.has("arrowleft"))  adx -= 1;
  if (keys.has("arrowright")) adx += 1;

  // Mouse-edge = PEEK → nudge while the cursor is at the edge, ease back after.
  // Skipped when the mouse is outside the viewport, a modal/menu owns input
  // (isAnyModalOpen — build shelf, craft/tavern/loot/settings/help, armed
  // placement), or the cursor is over a HUD surface rather than the canvas
  // (isPointerOverCanvas — the guard that lets this ship ON by default).
  let edx = 0, edz = 0;
  if (getEdgePanEnabled() && mouseClientX >= 0 && mouseClientY >= 0 && !isAnyModalOpen()) {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const atEdge =
      mouseClientX <= EDGE_PAN_PX || mouseClientX >= w - EDGE_PAN_PX ||
      mouseClientY <= EDGE_PAN_PX || mouseClientY >= h - EDGE_PAN_PX;
    if (atEdge && isPointerOverCanvas(mouseClientX, mouseClientY)) {
      if (mouseClientX <= EDGE_PAN_PX)             edx -= 1;
      else if (mouseClientX >= w - EDGE_PAN_PX)    edx += 1;
      if (mouseClientY <= EDGE_PAN_PX)             edz += 1;
      else if (mouseClientY >= h - EDGE_PAN_PX)    edz -= 1;
    }
  }

  const deliberate = adx !== 0 || adz !== 0;
  const dx = adx + edx;
  const dz = adz + edz;
  if (dx === 0 && dz === 0) return;

  if (deliberate) {
    // Arrow-driven scout → park (free-look until recenter). Announce the mode the
    // first time we leave follow-lock so the player knows how to snap back.
    if (cameraLocked) flashToast("Free-look — press Space to snap back to your hero");
    cameraLocked = false;
  } else if (cameraLocked) {
    // Edge-only nudge while still locked → a transient peek. Flag this frame so
    // the follow loop suppresses its pull; it resumes (easing home) once the
    // cursor leaves the edge. Stays locked, so an accidental brush self-heals.
    edgePeeking = true;
  }
  // (If already parked, edge-pan just free-pans the parked camera.)

  camera.getWorldDirection(_fwd);
  _fwd.y = 0;
  if (_fwd.lengthSq() < 1e-6) return;
  _fwd.normalize();
  _right.crossVectors(_fwd, _up).normalize();

  const len = Math.hypot(dx, dz);
  const speed = BASE_PAN_SPEED * getCameraSpeedMultiplier() * dt;
  _pan
    .copy(_fwd).multiplyScalar((dz / len) * speed)
    .addScaledVector(_right, (dx / len) * speed);

  const nextX = THREE.MathUtils.clamp(controls.target.x + _pan.x, PAN_MIN, PAN_MAX);
  const nextZ = THREE.MathUtils.clamp(controls.target.z + _pan.z, PAN_MIN, PAN_MAX);
  const appliedX = nextX - controls.target.x;
  const appliedZ = nextZ - controls.target.z;
  controls.target.x += appliedX;
  controls.target.z += appliedZ;
  camera.position.x += appliedX;
  camera.position.z += appliedZ;
}

let lastFrame = performance.now();

function frame(now: number) {
  const dt = Math.min((now - lastFrame) / 1000, 0.1);
  lastFrame = now;

  // FPS / ping readout — sample over a ~0.5s window so the number is steady;
  // update() is a no-op (no DOM write) while the overlay is hidden.
  fpsFrames++;
  const fpsElapsed = now - fpsSampleStart;
  if (fpsElapsed >= 500) {
    fpsOverlay.update((fpsFrames * 1000) / fpsElapsed, latestPingMs);
    fpsFrames = 0;
    fpsSampleStart = now;
  }

  panCamera(dt);
  // Ease the camera distance toward the wheel-zoom target every frame — both
  // while locked/following AND while parked (independent of panCamera's XZ/Y
  // pan, which preserves the camera→target offset exactly; see
  // applyZoomDamping's header comment). Must run before the controls.update()
  // call at the end of frame() so OrbitControls' internal spherical re-derives
  // from the eased position, not the pre-ease one.
  applyZoomDamping(dt);

  // Ability target-marker tracking. Reuses the shared `raycaster` and the
  // tracked mouse position from the edge-pan listener; runs only when the
  // cast cursor is armed AND the world+terrain are ready.
  if (castCursor && abilityMarker && world && mouseClientX >= 0 && mouseClientY >= 0 && castCursor.armed()) {
    const rect = canvas.getBoundingClientRect();
    ndc.x = ((mouseClientX - rect.left) / rect.width) * 2 - 1;
    ndc.y = -((mouseClientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(ndc, camera);
    const hits = raycaster.intersectObject(world.terrain, false);
    if (hits.length > 0) {
      const hit = hits[0]!;
      abilityMarker.hover({ x: hit.point.x, y: hit.point.y, z: hit.point.z });
    } else {
      abilityMarker.hover(null);
    }
  } else if (abilityMarker && !castCursor?.armed()) {
    abilityMarker.hover(null);
  }

  // World hover tooltip. Suppressed while a UI flow owns the cursor (placement,
  // armed cast, any open modal), during a mouse-drag, when dead, or after match
  // end. Re-picks on cursor move or every ~6 frames so live HP/amount track.
  if (hoverTooltip) {
    hoverFrameCounter++;
    const myCube = room && mySessionId ? room.state.cubes?.get(mySessionId) : null;
    // Hard/UI blockers clear the hover entirely → default cursor + no tooltip:
    // no world, off-screen, an open modal, dead, or match over.
    const blocked =
      !world || !room || mouseClientX < 0 ||
      Boolean(zmenu?.isOpen()) || Boolean(menu?.isOpen()) ||
      Boolean(craftMenu?.isOpen()) || Boolean(tavernShop?.isOpen()) || Boolean(corpseLoot?.isOpen()) ||
      room?.state.matchState === "ended" ||
      myCube?.state === "dead";
    if (blocked) {
      lastHoverContent = null;
      lastHoverX = -1;
      lastHoverY = -1;
    } else if (!mouseDown && !placement?.active() && !castCursor?.armed()) {
      // Re-pick on cursor move or every ~6 frames — but NOT while a button is
      // held: keeping the last hover stops the attack/harvest cursor flickering
      // to default the instant you click a target (and holds it during a pan).
      if (mouseClientX !== lastHoverX || mouseClientY !== lastHoverY || hoverFrameCounter % 6 === 0) {
        lastHoverContent = computeHover();
        lastHoverX = mouseClientX;
        lastHoverY = mouseClientY;
      }
    }
    // The tooltip panel additionally hides while dragging, placing, or aiming a
    // cast; the cursor (set below) still reflects the held hover.
    const tooltipHidden =
      blocked || mouseDown || Boolean(placement?.active()) || Boolean(castCursor?.armed());
    hoverTooltip.update(tooltipHidden ? null : lastHoverContent, mouseClientX, mouseClientY);
  }

  // Click-pinned inspect panel — live HP/gear/stance + auto-close on target loss.
  refreshInspect();

  cursorController.set(resolveCursorState());

  if (room && world && room.state.cubes) {
    // Day/night: lerp the scene lighting + refresh the HUD clock from the
    // server-synced cycle counter (instantaneous phase flip; the 20s blend is
    // client-side, computed in day-night-cycle.ts).
    dayNightVisual.apply(room.state.dayNightPhase, room.state.cycleElapsed);
    dayNightClock.update({
      phase: room.state.dayNightPhase,
      cycleElapsed: room.state.cycleElapsed,
      dayCount: room.state.dayCount,
    });

    // Win-condition standing: living-player count per team. Read the SERVER-
    // authored scalars (state.team0Alive/team1Alive), NOT a client-side count of
    // `cubes` — enemy cubes are fog-of-war view-culled, so counting locally would
    // miss every unscouted enemy and read a false 0. Map ally/enemy off my team.
    // Hidden until the local cube exists (so we know which team is "ally") and
    // while a match is running (no point during lobby / end screen).
    {
      const me = mySessionId ? room.state.cubes.get(mySessionId) : undefined;
      const allies = me ? (me.teamId === 0 ? room.state.team0Alive : room.state.team1Alive) : 0;
      const enemies = me ? (me.teamId === 0 ? room.state.team1Alive : room.state.team0Alive) : 0;
      winReadout.update({ allies, enemies, show: !!me && room.state.matchState === "running" });
    }

    players.forEach((player, sessionId) => {
      const cube = room!.state.cubes.get(sessionId);
      if (!cube) return;
      const isLocal = sessionId === mySessionId;

      // Render-position resolution — buffered snapshot interpolation for ALL
      // units, local hero included (the Dota model: command feedback is cosmetic,
      // position is never predicted). predRenderX/Z carry the position to draw;
      // predMoving drives the walk anim + facing.
      let predRenderX = cube.x;
      let predRenderZ = cube.z;
      let predMoving = false;
      const interpActive = Number.isFinite(cube.x) && Number.isFinite(cube.z);
      if (interpActive) {
        // FIX A — render this unit INTERP_DELAY_MS behind the latest snapshot and
        // lerp between the two authoritative snapshots that bracket that render
        // time (Source-engine cl_interp). Turns 30 Hz data into smooth per-frame
        // motion; no predicted mesh lead (click feedback stays with the gold ring
        // + server ack).
        // Buffer is fed by the per-cube onChange (FIX 3b) at patch-arrival time,
        // not here — recording in the render loop would re-quantize to the frame
        // clock. Lazy-create only as a fallback (cube present before its onAdd
        // wired a buffer); the onChange handles all subsequent records.
        let buf = interpBuffers.get(sessionId);
        if (!buf) { buf = new SnapshotBuffer(); interpBuffers.set(sessionId, buf); }
        const s = buf.sample(now - INTERP_DELAY_MS);
        if (s) { predRenderX = s.x; predRenderZ = s.z; }
        // FIX B — run/idle from ACTUAL rendered speed, not the server target
        // delta (which lingers true after arrival → "running in place"). On
        // arrival the buffer freezes → displacement decays to exactly 0 → idle.
        const prev = player.object.position;
        const spd = dt > 0 ? Math.hypot(predRenderX - prev.x, predRenderZ - prev.z) / dt : 0;
        predMoving = spd > RENDER_MOVE_EPS;
      }

      const dx = cube.targetX - cube.x;
      const dz = cube.targetZ - cube.z;
      const moving = predMoving;
      // Optimistic attack windup while chasing: fire the swing pose the instant
      // the rendered hero reaches the target, so a combat approach doesn't
      // idle-then-swing while the last patches interpolate in.
      if (isLocal) tickChaseWindup(predRenderX, predRenderZ, moving);
      if (cube.state === "dead") {
        player.setAction("dead");
        attackingUntil.delete(sessionId);
      } else {
        const hold = attackingUntil.get(sessionId);
        if (hold !== undefined && Date.now() < hold) {
          // attacking still playing — let the swing pose finish before walk/idle resumes
        } else {
          if (hold !== undefined) attackingUntil.delete(sessionId);
          // Gathering: server sets cube.harvestTargetId the moment a harvest order
          // is issued, but the chop pose should only play once the kobold has
          // ARRIVED and is standing at the node (not while walking up to it).
          if (cube.harvestTargetId && !moving) {
            player.setAction("gathering");
          } else {
            // Choose walk vs run based on V-toggle (walkToggled = slow walk saves stamina).
            player.setAction(moving ? (cube.survival?.walkToggled ? "walking" : "running") : "idle");
          }
        }
      }
      // Facing: while walking, face the walk heading. While standing still and
      // gathering or mid-swing, turn to FACE the node/creature/building being hit
      // (playtest 2026-06-12 — a chop/attack pose pointing the wrong way looks
      // broken). Gather uses the live harvestTargetId; attack uses the last swing
      // target, but only while the swing pose is active so we don't keep staring
      // at a stale target after combat ends.
      if (moving) {
        // Face the rendered heading (this frame's render displacement) — the
        // render position is what's actually visible, so it's the correct
        // heading; fall back to the server target delta on a zero-displacement
        // frame.
        const hdx = predRenderX - player.object.position.x;
        const hdz = predRenderZ - player.object.position.z;
        if (Math.hypot(hdx, hdz) > 1e-4) player.setRotation(Math.atan2(hdx, hdz));
        else if (Math.hypot(dx, dz) > 1e-4) player.setRotation(Math.atan2(dx, dz));
      } else {
        let tx: number | undefined, tz: number | undefined;
        if (cube.harvestTargetId) {
          const node = room!.state.resources.get(cube.harvestTargetId);
          if (node) { tx = node.x; tz = node.z; }
        }
        if (tx === undefined) {
          const atkHold = attackingUntil.get(sessionId);
          if (atkHold !== undefined && Date.now() < atkHold) {
            const atkId = attackTargetById.get(sessionId);
            const t = atkId
              ? (room!.state.cubes?.get(atkId) ?? room!.state.creatures?.get(atkId) ?? room!.state.buildings?.get(atkId))
              : undefined;
            if (t) { tx = t.x; tz = t.z; }
          }
        }
        if (tx !== undefined && tz !== undefined) {
          const fdx = tx - cube.x, fdz = tz - cube.z;
          if (Math.hypot(fdx, fdz) > 1e-3) player.setRotation(Math.atan2(fdx, fdz));
        }
      }
      // Write the interpolated render position. Mirrors the onAdd NaN-guard: if
      // a schema hiccup leaves cube.x/z as NaN, skip the write rather than
      // poisoning player.object.position forever (NaN propagates every frame).
      if (Number.isFinite(predRenderX) && Number.isFinite(predRenderZ)) {
        player.object.position.x = predRenderX;
        player.object.position.z = predRenderZ;
      }
      player.object.position.y = world!.sampleHeight(player.object.position.x, player.object.position.z);
      // Task 9: item-dash trail + arrival VFX, driven by rendered position delta
      // (a dash moves far faster than a walk → the detector spawns a trail puff
      // each dashing frame + an arrival burst the frame the cube stops).
      tickDashTrailVfx(
        sessionId,
        player.object.position.x, player.object.position.y, player.object.position.z,
        dt * 1000, scene, dashVfx,
      );
      const me = mySessionId ? room!.state.cubes.get(mySessionId) : null;
      if (me) {
        const isOwn = sessionId === mySessionId;
        const desired = tintFor(me, cube, isOwn);
        player.setTint(desired);
        // Task 7: local player translucency while stealthed.
        if (isOwn) player.setOpacity(hasStealthBuff(cube) ? 0.4 : 1.0);
      }
      player.mixer.update(dt);
    });

    // Floating overhead UI — sprite bars + DOM rich overlays.
    if (floaterManager && world && mySessionId) {
      floaterManager.tick({
        state: room.state,
        mySessionId,
        camera,
        sampleHeight: world.sampleHeight,
        attackTargetId,
        players,  // lerped client positions keep bars in sync with visible mesh
        // Gate resource HP bars to live vision; pre-match (fog null) → show bars.
        isVisibleAt: (x: number, z: number) => (fog ? fog.sampleFog(x, z).visible > 0.5 : true),
      });
    }

    // Slice D placeholder world-VFX — advance every active shape (one-shot
    // fades + persistent toggle/channel follow).
    castVfx?.tick(dt * 1000);

    // Pet beam one-shots (Nightwisp) — tick + cull expired beams each frame.
    for (let i = petBeams.length - 1; i >= 0; i--) {
      if (!petBeams[i]!.tick(dt * 1000)) {
        scene.remove(petBeams[i]!.object);
        petBeams[i]!.dispose();
        petBeams.splice(i, 1);
      }
    }

    // Projectile impact poof one-shots — tick + cull expired poofs each frame.
    for (let i = projectilePoofs.length - 1; i >= 0; i--) {
      if (!projectilePoofs[i]!.tick(dt * 1000)) {
        scene.remove(projectilePoofs[i]!.object);
        projectilePoofs[i]!.dispose();
        projectilePoofs.splice(i, 1);
      }
    }

    // Task 9: item-dash trail + arrival one-shots — tick + cull each frame.
    for (let i = dashVfx.length - 1; i >= 0; i--) {
      if (!dashVfx[i]!.tick(dt * 1000)) {
        scene.remove(dashVfx[i]!.object);
        dashVfx[i]!.dispose();
        dashVfx.splice(i, 1);
      }
    }

    // Audio — listener tracks the camera's orbit target (the point the MOBA
    // camera watches), facing the camera's ground-plane forward so L/R panning
    // follows the view as the player yaws it; the director advances bed
    // crossfades + survival polling.
    if (audio) {
      audio.setListener(controls.target, {
        x: controls.target.x - camera.position.x,
        z: controls.target.z - camera.position.z,
      });
      audioDirector?.tick(dt);
    }

    // Fog of war — recompute the shroud from the local team's living vision
    // sources. The client only receives own-team + currently-visible enemy
    // cubes, so filtering by my teamId yields exactly the team's sources. Same
    // shared vision math (+ same heightmap) the server gates with, so the lit
    // region matches where enemies can actually appear.
    if (fog && mySessionId) {
      const me = room.state.cubes.get(mySessionId);
      if (me) {
        const myTeam = me.teamId;
        // Match the server's night-scaled vision so the fog and entity gating agree
        // (B5 Night Eye negates the night penalty for a Foraging-tier-5 scout).
        const nightness = room.state.dayNightPhase === 1 ? 1 : 0;
        const sources: VisionSource[] = [];
        room.state.cubes.forEach((c) => {
          if (c.teamId === myTeam && c.state === "alive") {
            sources.push({ x: c.x, z: c.z, radius: sightRadius(nightness, (c.expertise?.foraging ?? 0) >= 5) });
          }
        });
        fog.tick(sources, now);
      }
    }
    minimap?.update();

    if (mySessionId) {
      const me = room.state.cubes.get(mySessionId);
      if (me) {
        // Centered respawn overlay (HP / Mana / Stamina now live on the v10 plate).
        hud?.update({ state: me.state, respawnAt: me.respawnAt });
        // v10 plate mirrors equipment, backpack, HP, mana, stamina, survival,
        // XP ring, level badge — single refresh per frame off schema state.
        mainPlate?.refresh(me);
        // Task 7: stealth pip visibility.
        stealthPip.style.display = hasStealthBuff(me) ? "block" : "none";
        const myPets: Array<{ creatureType: string; hp: number; hpMax: number; level: number; dmg: number }> = [];
        room.state.creatures?.forEach((c) => {
          if (c.ownerId === mySessionId) myPets.push({ creatureType: c.creatureType, hp: c.hp, hpMax: c.hpMax, level: c.level, dmg: c.dmg });
        });
        const petCapMod = me.mods?.get?.("petCap");
        const masteryMask = getMod(me, MASTERY_MOD_KEY);
        petFrame?.refresh({ pets: myPets, petCap: typeof petCapMod === "number" ? petCapMod : 1, masteryMask });
        goldCounter?.refresh(me);
        expertiseArc?.refresh(me);
        craftMenu?.refresh(me);
        tavernShop?.refresh(me);
        onboarding?.update({ cube: me, state: room.state, mySessionId });
        // Level-up banner — fire on a genuine increase (skip the initial sync).
        const selfLevel = me.expertise?.characterLevel ?? 1;
        if (lastSelfLevel >= 0 && selfLevel > lastSelfLevel) flashLevelUp(selfLevel);
        lastSelfLevel = selfLevel;
      }
      zmenu?.refresh(me ?? null);
      renderWalkStateIcon(me);
    }

    if (hud) hud.tickDamage(camera);

    if (mySessionId && room) {
      const me = room.state.cubes.get(mySessionId);
      if (me && me.state === "dead") {
        // Dead: follow a living teammate (death-cam). Pans regardless of lock so
        // you always see the action while waiting to respawn.
        let watchId: string | null = null;
        room.state.cubes.forEach((c, sid) => {
          if (sid === mySessionId) return;
          if (c.teamId !== me.teamId) return;
          if (c.state !== "alive") return;
          if (watchId === null) watchId = sid;
        });
        if (watchId) {
          const watched = room.state.cubes.get(watchId);
          if (watched) {
            const t = followTarget(watched.x, watched.z);
            const gy = world ? world.sampleHeight(watched.x, watched.z) : undefined;
            panCameraTo(t.x, t.z, followAlpha(dt), gy);
          }
        }
        // If no living teammate, leave camera alone — free orbit.
      } else if (me && cameraLocked && !edgePeeking) {
        // Alive + locked (and not mid edge-peek): MOBA follow — keep the local
        // kobold framed above the HUD. While edgePeeking the pull is suppressed so
        // the peek holds; dropping the suppression next frame eases the view home.
        // Follow the PREDICTED hero (the local mesh position, written this frame)
        // so the camera doesn't lag behind the responsive hero under latency.
        const lobj = players.get(mySessionId)?.object;
        const hx = lobj ? lobj.position.x : me.x;
        const hz = lobj ? lobj.position.z : me.z;
        const t = followTarget(hx, hz);
        const gy = world ? world.sampleHeight(hx, hz) : undefined;
        panCameraTo(t.x, t.z, followAlpha(dt), gy);
      }

      // Whole-canopy occluder fade — scans camera→hero for the SPECIFIC doodad
      // instance(s) actually standing in the way and fades just those (the
      // screen-space hero-porthole circle this used to run alongside was
      // deleted 2026-07-02: buildings never opted into it, and it stacked
      // with this fade to re-punch holes through canopies the fade already
      // handles — see toon.ts / git history for the removed circle path).
      // Disabled while dead / not loaded. Uses the predicted mesh position so
      // the scan tracks the responsive hero.
      if (me && me.state === "alive" && world) {
        const lobj = players.get(mySessionId)?.object;
        const cx = lobj ? lobj.position.x : me.x;
        const cz = lobj ? lobj.position.z : me.z;
        const groundY = world.sampleHeight(cx, cz);

        if (!canopyGateOpen) {
          // Gate reopened (respawn / world (re)loaded) — resume scanning,
          // respecting whatever the debug toggle currently says. Mirrors the
          // close below.
          canopyGateOpen = true;
          canopyFade?.setEnabled(canopyFadeEnabledDebug);
        }
        canopyFadeUniforms.uCanopyFadeEnabled.value = canopyFadeEnabledDebug ? 1 : 0;
        if (canopyFade && canopyFadeEnabledDebug) {
          _canopyFootV.set(cx, groundY, cz);
          canopyFade.update(dt * 1000, camera, _canopyFootV, doodadObjectsRoot);
        }
      } else if (canopyGateOpen) {
        // Hero died / world unloaded mid-fade (bug fix 2026-07-02): the branch
        // above stops running entirely, so without this any in-flight ghosted
        // canopies would freeze at their last fade value — stuck transparent
        // behind the death overlay — instead of easing back to solid.
        // setEnabled(false) snaps every tracked instance back to fully solid
        // AND clears the controller's active map immediately (see
        // canopy-fade.ts); re-enabled above (respecting the debug toggle) the
        // moment the gate reopens on respawn.
        canopyGateOpen = false;
        canopyFade?.setEnabled(false);
      }

      // Per-unit x-ray silhouette occlusion gating (bug fix 2026-07-02): runs
      // unconditionally (not nested in the `me alive` branch above) so every
      // registered unit's own throttled raycast — teammates, enemies, pets —
      // still gates correctly while the local hero is dead/spectating. See
      // xray-silhouette.ts for why this replaced "always visible".
      {
        _xrayOccluders.length = 0;
        if (doodadObjectsRoot) _xrayOccluders.push(doodadObjectsRoot);
        if (buildingRender) _xrayOccluders.push(buildingRender.group);
        if (resources) _xrayOccluders.push(resources.group);
        // Faded-canopy precedence (2026-07-02): an instance canopy-fade has
        // already ghosted must not also trip the x-ray gold tint — see
        // xray-silhouette.ts header. Now that the debug toggle actually calls
        // canopyFade.setEnabled() (see above) instead of only flipping the
        // shader uniform, flipping it off promptly clears the controller's
        // active map — isInstanceFaded starts returning false again the same
        // frame, so a solid (un-faded) tree correctly resumes gating x-ray.
        const isDoodadFaded = canopyFade ? canopyFade.isInstanceFaded : undefined;
        updateXrayOcclusion(dt * 1000, camera, _xrayOccluders, isDoodadFaded);
      }

      // Off-screen hero indicator (issue #5): when the camera is parked/peeked
      // away and the hero leaves the frame, point an edge arrow back to it.
      // Hidden whenever the hero is on-screen (the locked-follow common case).
      if (offscreenHero) {
        if (me && me.state === "alive" && world) {
          const hy = world.sampleHeight(me.x, me.z) + 1.0;
          _heroProj.set(me.x, hy, me.z).project(camera);
          const behind = _heroProj.z > 1; // point is behind the camera → NDC mirrored
          const nx = behind ? -_heroProj.x : _heroProj.x;
          const ny = behind ? -_heroProj.y : _heroProj.y;
          const off = behind || nx < -1 || nx > 1 || ny < -1 || ny > 1;
          offscreenHero.update(off ? { nx, ny } : null);
        } else {
          offscreenHero.update(null);
        }
      }

      // Directional edge arrows (feedback #7): campfire-while-freezing + teammates.
      if (directionArrows) {
        directionArrows.update(
          me && me.state === "alive" && world
            ? { me, state: room.state, mySessionId, camera, sampleHeight: world.sampleHeight }
            : null,
        );
      }
    }

    if (creatureRender && room.state.creatures) {
      const meCube = mySessionId ? room.state.cubes.get(mySessionId) : undefined;
      const creatureMe = meCube && mySessionId ? { sessionId: mySessionId, teamId: meCube.teamId } : null;
      creatureRender.reconcile(room.state.creatures.entries(), creatureMe);
      // Same interp sample clock as players (FIX A) — creatures render
      // INTERP_DELAY_MS behind now off their own snapshot buffers (D3).
      creatureRender.update(dt, now - INTERP_DELAY_MS);
    }

    if (projectileRender && room.state.projectiles) {
      projectileRender.reconcile(room.state.projectiles);
    }

    if (buildingRender && room.state.buildings) {
      buildingRender.reconcile(room.state.buildings.entries());
      buildingRender.update(dt);
    }

    if (corpseRender && room.state.corpses) {
      corpseRender.reconcile(room.state.corpses.entries());
    }

    if (corpseLoot && corpseLoot.isOpen()) {
      corpseLoot.refresh(room.state);
    }

    if (menu && menu.isOpen()) {
      const id = menu.currentId();
      const b = id ? room.state.buildings?.get(id) : null;
      const me = mySessionId ? room.state.cubes?.get(mySessionId) : null;
      if (!b || b.state === "destroyed" || !me) {
        menu.close();
      } else {
        menu.refresh(b, menuOpts(b, me));
      }
    }

    // Wonder timer HUD + end-screen overlay. wonderHud.update is cheap (textContent
    // set when active, display:none when deadline === 0). endScreen.show is
    // idempotent — calling it every frame while matchState === "ended" rewrites
    // the same DOM. Result is computed locally from teamId vs winningTeamId so we
    // don't need a server-pushed label.
    if (wonderHud) {
      wonderHud.update({
        deadline: room.state.wonderDeadlineMs,
        extended: room.state.wonderTimerExtended,
        now: Date.now(),
      });
    }
    if (endScreen) {
      if (room.state.matchState === "ended") {
        const me = mySessionId ? room.state.cubes.get(mySessionId) : null;
        const winningTeam = room.state.winningTeamId;
        if (me) {
          const result: "victory" | "defeat" | "draw" =
            winningTeam === 255 ? "draw" : me.teamId === winningTeam ? "victory" : "defeat";
          endScreen.show({ result, winningTeamId: winningTeam });
        }
      } else {
        // matchState flipped back to "running" — covers debug `match.restart` so
        // the modal disappears without a page reload.
        endScreen.hide();
      }
    }
  }

  if (indicatorTime > 0 && world) {
    indicatorTime -= dt;
    const t = Math.max(0, indicatorTime / INDICATOR_DURATION);
    const grow = 1 + (1 - t) * 1.6;
    conformGroundRing(indicatorRing, indicatorX, indicatorZ, grow);
    (indicatorRing.mesh.material as THREE.MeshBasicMaterial).opacity = t * 0.85;
    if (indicatorTime <= 0) indicatorRing.mesh.visible = false;
  } else {
    indicatorRing.mesh.visible = false;
  }

  if (rejectedTime > 0 && world) {
    rejectedTime -= dt;
    const t = Math.max(0, rejectedTime / REJECTED_DURATION);
    // Contract (opposite of the move ring's grow) + double-blink: |sin| dips the
    // opacity to 0 twice over the lifetime, which reads as "no" at a glance.
    const shrink = 1 + t * 0.9;
    conformGroundRing(rejectedRing, rejectedX, rejectedZ, shrink);
    const blink = Math.abs(Math.sin((1 - t) * Math.PI * 2));
    (rejectedRing.mesh.material as THREE.MeshBasicMaterial).opacity = t * 0.9 * blink;
    if (rejectedTime <= 0) rejectedRing.mesh.visible = false;
  } else {
    rejectedRing.mesh.visible = false;
  }

  const reticleMat = attackReticle.mesh.material as THREE.MeshBasicMaterial;
  if (reticleTime > 0 && world && room) {
    reticleTime -= dt;
    // Resolve the live target position so the ring tracks a moving creature/
    // player; fall back to the last-known point if the target despawned/died.
    let rx: number | null = null;
    let rz: number | null = null;
    if (reticleTarget) {
      const ent =
        reticleTarget.kind === "cube" ? room.state.cubes?.get(reticleTarget.id) :
        reticleTarget.kind === "creature" ? room.state.creatures?.get(reticleTarget.id) :
        room.state.buildings?.get(reticleTarget.id);
      if (ent) { rx = ent.x; rz = ent.z; reticleStatic = { x: ent.x, z: ent.z }; }
      else if (reticleStatic) { rx = reticleStatic.x; rz = reticleStatic.z; }
    } else if (reticleStatic) {
      rx = reticleStatic.x; rz = reticleStatic.z;
    }
    if (rx !== null && rz !== null) {
      const t = Math.max(0, reticleTime / RETICLE_DURATION);
      conformGroundRing(attackReticle, rx, rz, 1 + t * 0.6); // contracts 1.6 → 1.0 (lock-on)
      reticleMat.opacity = Math.min(0.9, t * 1.6);
    } else {
      reticleMat.opacity = 0;
      attackReticle.mesh.visible = false;
    }
  } else if (reticleMat.opacity !== 0 || attackReticle.mesh.visible) {
    reticleMat.opacity = 0;
    attackReticle.mesh.visible = false;
  }

  controls.update();

  // Throttle the shadow-map re-render to every SHADOW_UPDATE_INTERVAL frames. We
  // also only re-lock the shadow ortho frustum to OrbitControls.target on those
  // frames, so the rendered shadow map and its sampling matrix stay in sync (no
  // shadow "swim" between updates). SUN_OFFSET keeps the light DIRECTION constant,
  // so freezing sun.position/target on skipped frames doesn't change diffuse
  // lighting — only the shadow window re-centres, lagging ≤1 frame while panning.
  const updateShadow = shadowFrame++ % SHADOW_UPDATE_INTERVAL === 0;
  if (updateShadow) {
    sun.position.copy(controls.target).add(SUN_OFFSET);
    sun.target.position.copy(controls.target);
    sun.target.updateMatrixWorld();
    sun.shadow.camera.updateProjectionMatrix();
  }
  renderer.shadowMap.needsUpdate = updateShadow;

  // Drive the grass-tuft wind shader from the same frame clock.
  if (world?.grass) world.grass.update(dt);
  // Animate home-fire flame flicker + beacon column pulse.
  if (world?.homeFires) world.homeFires.update(dt);
  // Drive the foliage (tree canopy + fern) wind sway from the same clock.
  foliageWind.uTime.value += dt;

  renderer.render(scene, camera);

  // First fully-painted frame of the live scene → lift the loading gate (fades
  // out). Dismissing here (not at the end of initGameClient) guarantees the
  // player sees real pixels, not a black flash, when the overlay clears.
  if (dismissLoadingOnFrame) {
    dismissLoadingOnFrame = false;
    loadingScreen.finish();
  }

  requestAnimationFrame(frame);
}

// The render loop is NOT kicked at module load — it starts at the end of
// initGameClient (only once a joined room is "running"). Until then the empty
// scene is never rendered; the landing/lobby menus are DOM overlays.
